// src/lib/i18n.js — multi-language registry (EN, HI, KN, TA, BN, GU, MR, PA)
import React, { createContext, useContext, useState } from "react";
import { Logo } from "../components/common";

export const LanguageContext = createContext({ lang:"en", toggle:()=>{}, choose:()=>{} });

// ─── Language picker screen ───────────────────────────────────────────────────
export const LANG_OPTIONS = [
  { code:"en", label:"English",  native:"English"  },
  { code:"hi", label:"Hindi",    native:"हिंदी"     },
  { code:"mr", label:"Marathi",  native:"मराठी"     },
  { code:"gu", label:"Gujarati", native:"ગુજરાતી"   },
  { code:"pa", label:"Punjabi",  native:"ਪੰਜਾਬੀ"    },
  { code:"bn", label:"Bengali",  native:"বাংলা"     },
  { code:"ta", label:"Tamil",    native:"தமிழ்"     },
  { code:"kn", label:"Kannada",  native:"ಕನ್ನಡ"     },
];

function LangPicker({ onChoose, onClose }) {
  return (
    <>
      {/* Backdrop */}
      <div style={{position:"fixed",inset:0,background:"rgba(6,9,15,.72)",zIndex:10002,backdropFilter:"blur(6px)",WebkitBackdropFilter:"blur(6px)"}} onClick={onClose||undefined}/>
      {/* Modal card — centered */}
      <div style={{
        position:"fixed", top:"50%", left:"50%", transform:"translate(-50%,-50%)",
        width:"min(480px,calc(100vw - 32px))",
        background:"linear-gradient(160deg,#13203A 0%,#0C1628 100%)",
        border:"1px solid rgba(255,255,255,.18)", borderRadius:22,
        boxShadow:"0 32px 80px rgba(0,0,0,.7)",
        zIndex:10003, padding:"36px 24px 28px",
        fontFamily:"'DM Sans',sans-serif",
        animation:"fadeIn .22s ease",
      }}>
        {/* Close button */}
        {onClose && <button onClick={onClose} style={{position:"absolute",top:14,right:14,width:30,height:30,borderRadius:"50%",background:"rgba(255,255,255,.12)",border:"1px solid rgba(255,255,255,.2)",color:"rgba(255,255,255,.8)",fontSize:17,lineHeight:1,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>}
        {/* Logo + headings */}
        <div style={{textAlign:"center",marginBottom:24}}>
          <Logo size="md" light={true}/>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,color:"#fff",fontWeight:600,marginTop:16,letterSpacing:"-0.01em"}}>
            Choose your language
          </div>
          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:13,color:"rgba(255,255,255,.65)",marginTop:6}}>
            भाषा चुनें · ಭಾಷೆ ಆರಿಸಿ · மொழி தேர்வு
          </div>
        </div>
        {/* 4×2 grid */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
          {LANG_OPTIONS.map(l => (
            <button key={l.code} onClick={()=>onChoose(l.code)} style={{
              padding:"14px 6px", borderRadius:13,
              border:"1.5px solid rgba(255,255,255,.22)",
              background:"rgba(255,255,255,.08)", cursor:"pointer",
              display:"flex", flexDirection:"column", alignItems:"center", gap:5,
              transition:"background .15s, border-color .15s, transform .12s",
            }}
              onMouseOver={e=>{e.currentTarget.style.background="rgba(255,255,255,.18)";e.currentTarget.style.borderColor="rgba(255,255,255,.5)";e.currentTarget.style.transform="translateY(-2px)";}}
              onMouseOut={e=>{e.currentTarget.style.background="rgba(255,255,255,.08)";e.currentTarget.style.borderColor="rgba(255,255,255,.22)";e.currentTarget.style.transform="";}}>
              <span style={{fontSize:18,lineHeight:1,color:"#fff",fontWeight:500}}>{l.native}</span>
              <span style={{fontFamily:"'DM Mono',monospace",fontSize:8,color:"rgba(255,255,255,.6)",letterSpacing:"0.08em",textTransform:"uppercase"}}>{l.label}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── Provider ─────────────────────────────────────────────────────────────────
export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem("thc-lang") || null; } catch { return null; }
  });
  const [pickerOpen, setPickerOpen] = useState(false);

  const choose = (l) => {
    setLang(l);
    setPickerOpen(false);
    try { localStorage.setItem("thc-lang", l); } catch {}
  };

  const openPicker = () => setPickerOpen(true);

  return (
    <LanguageContext.Provider value={{ lang: lang||"en", choose, openPicker }}>
      {children}
      {(!lang || pickerOpen) && <LangPicker onChoose={choose} onClose={lang ? ()=>setPickerOpen(false) : null}/>}
    </LanguageContext.Provider>
  );
}

export function useLanguage() { return useContext(LanguageContext); }

// ─── Hero lines per language ──────────────────────────────────────────────────
export const HERO_LINES = {
  en: [
    { top:"Your nani's watch",     accent:"deserves your wrist." },
    { top:"Your dadi's HMT Priya", accent:"still ticks." },
    { top:"Chacha's field watch",  accent:"never forgot the time." },
    { top:"Papa's dress watch",    accent:"is now yours." },
    { top:"Mama's Allwyn",         accent:"more precious now." },
    { top:"Your tauji's HMT",      accent:"deserves fresh love." },
    { top:"Dada's everyday watch", accent:"has a story." },
    { top:"Your nana's Ricoh",     accent:"find it again." },
    { top:"The watch mausi wore",  accent:"to your wedding." },
    { top:"A piece of India",      accent:"on your wrist." },
  ],
  hi: [
    { top:"नानी की घड़ी",            accent:"आपकी कलाई की हकदार है।" },
    { top:"दादी की HMT प्रिया",      accent:"आज भी चलती है।" },
    { top:"चाचा की फ़ील्ड घड़ी",     accent:"वक़्त कभी नहीं भूली।" },
    { top:"पापा की ड्रेस घड़ी",      accent:"अब आपकी है।" },
    { top:"माँ की Allwyn",            accent:"आज और क़ीमती है।" },
    { top:"ताऊजी की HMT",            accent:"को नई मोहब्बत चाहिए।" },
    { top:"दादा की रोज़ की घड़ी",    accent:"में एक कहानी है।" },
    { top:"नाना की Ricoh",            accent:"फिर से ढूंढें।" },
    { top:"वो घड़ी जो मौसी ने पहनी", accent:"आपकी शादी में।" },
    { top:"भारत का एक टुकड़ा",        accent:"आपकी कलाई पर।" },
  ],
  mr: [
    { top:"आजीची घड्याळ",            accent:"तुमच्या मनगटावर हवी." },
    { top:"आईची HMT प्रिया",          accent:"अजूनही चालते." },
    { top:"काकांची फील्ड घड्याळ",    accent:"वेळ कधी विसरली नाही." },
    { top:"बाबांची ड्रेस घड्याळ",    accent:"आता तुमची आहे." },
    { top:"आईची Allwyn",               accent:"आता अधिक मौल्यवान." },
    { top:"A piece of India",          accent:"on your wrist." },
    { top:"आजोबांची घड्याळ",           accent:"एक गोष्ट सांगते." },
    { top:"नाना की Ricoh",             accent:"पुन्हा शोधा." },
    { top:"मावशीने घातलेली घड्याळ",   accent:"तुमच्या लग्नात." },
    { top:"भारताचा एक तुकडा",          accent:"तुमच्या मनगटावर." },
  ],
  gu: [
    { top:"નાનીની ઘડિયાળ",            accent:"તમારા કાંડા પર હોવી જોઈએ." },
    { top:"દાદીની HMT પ્રિયા",         accent:"હજુ ચાલે છે." },
    { top:"ચાચાની ઘડિયાળ",             accent:"સમય ક્યારેય ભૂલ્યો નહીં." },
    { top:"પપ્પાની ઘડિયાળ",            accent:"હવે તમારી છે." },
    { top:"મમ્મીની Allwyn",             accent:"હવે વધુ કિંમતી." },
    { top:"A piece of India",           accent:"on your wrist." },
    { top:"દાદાની ઘડિયાળ",             accent:"એક વાર્તા ધરાવે છે." },
    { top:"નાનાની Ricoh",              accent:"ફરીથી શોધો." },
    { top:"માસીએ પહેરેલી ઘડિયાળ",     accent:"તમારા લગ્નમાં." },
    { top:"ભારતનો એક ટુકડો",           accent:"તમારા કાંડા પર." },
  ],
  pa: [
    { top:"ਨਾਨੀ ਦੀ ਘੜੀ",              accent:"ਤੁਹਾਡੀ ਗੁੱਟ ਦੀ ਹੱਕਦਾਰ ਹੈ।" },
    { top:"ਦਾਦੀ ਦੀ HMT ਪ੍ਰਿਯਾ",       accent:"ਅਜੇ ਵੀ ਚੱਲਦੀ ਹੈ।" },
    { top:"ਚਾਚੇ ਦੀ ਘੜੀ",              accent:"ਵਕਤ ਕਦੇ ਨਹੀਂ ਭੁੱਲੀ।" },
    { top:"ਪਾਪਾ ਦੀ ਘੜੀ",              accent:"ਹੁਣ ਤੁਹਾਡੀ ਹੈ।" },
    { top:"ਮਾਂ ਦੀ Allwyn",             accent:"ਹੁਣ ਹੋਰ ਕੀਮਤੀ ਹੈ।" },
    { top:"A piece of India",           accent:"on your wrist." },
    { top:"ਦਾਦੇ ਦੀ ਘੜੀ",              accent:"ਵਿੱਚ ਇੱਕ ਕਹਾਣੀ ਹੈ।" },
    { top:"ਨਾਨੇ ਦੀ Ricoh",             accent:"ਦੁਬਾਰਾ ਲੱਭੋ।" },
    { top:"ਉਹ ਘੜੀ ਜੋ ਮਾਸੀ ਨੇ ਪਾਈ",   accent:"ਤੁਹਾਡੇ ਵਿਆਹ ਵਿੱਚ।" },
    { top:"ਭਾਰਤ ਦਾ ਇੱਕ ਟੁਕੜਾ",        accent:"ਤੁਹਾਡੀ ਗੁੱਟ 'ਤੇ।" },
  ],
  bn: [
    { top:"নানির ঘড়ি",                accent:"তোমার কব্জির যোগ্য।" },
    { top:"দাদির HMT প্রিয়া",          accent:"এখনও চলে।" },
    { top:"কাকার ঘড়ি",                 accent:"সময় কখনো ভোলেনি।" },
    { top:"বাবার ঘড়ি",                 accent:"এখন তোমার।" },
    { top:"মায়ের Allwyn",              accent:"এখন আরও মূল্যবান।" },
    { top:"A piece of India",           accent:"on your wrist." },
    { top:"দাদার ঘড়ি",                 accent:"একটা গল্প বলে।" },
    { top:"নানার Ricoh",               accent:"আবার খুঁজে নাও।" },
    { top:"মাসি যে ঘড়ি পরেছিলেন",    accent:"তোমার বিয়েতে।" },
    { top:"ভারতের একটুকরো",            accent:"তোমার কব্জিতে।" },
  ],
  ta: [
    { top:"பாட்டியின் கடிகாரம்",       accent:"உங்கள் மணிக்கட்டுக்கு உரியது." },
    { top:"அம்மாவின் HMT பிரியா",      accent:"இன்னும் ஓடுகிறது." },
    { top:"சித்தப்பாவின் கடிகாரம்",   accent:"நேரத்தை மறக்கவில்லை." },
    { top:"அப்பாவின் கடிகாரம்",        accent:"இப்போது உங்களுடையது." },
    { top:"அம்மாவின் Allwyn",           accent:"இப்போது மேலும் விலைமதிப்பானது." },
    { top:"A piece of India",           accent:"on your wrist." },
    { top:"தாத்தாவின் கடிகாரம்",       accent:"ஒரு கதை சொல்கிறது." },
    { top:"நாட்டுப்பட்டி Ricoh",       accent:"மீண்டும் தேடுங்கள்." },
    { top:"மாமி அணிந்த கடிகாரம்",     accent:"உங்கள் திருமணத்தில்." },
    { top:"இந்தியாவின் ஒரு துண்டு",   accent:"உங்கள் மணிக்கட்டில்." },
  ],
  kn: [
    { top:"ನಾನಿಯ ಗಡಿಯಾರ",            accent:"ನಿಮ್ಮ ಮಣಿಕಟ್ಟಿಗೆ ಸಲ್ಲಲಿ." },
    { top:"ಅಜ್ಜಿಯ HMT ಪ್ರಿಯಾ",        accent:"ಇನ್ನೂ ನಡೆಯುತ್ತದೆ." },
    { top:"ಚಿಕ್ಕಪ್ಪನ ಗಡಿಯಾರ",         accent:"ಸಮಯ ಮರೆತಿಲ್ಲ." },
    { top:"ಅಪ್ಪನ ಗಡಿಯಾರ",             accent:"ಈಗ ನಿಮ್ಮದು." },
    { top:"ಅಮ್ಮನ Allwyn",              accent:"ಈಗ ಇನ್ನೂ ಅಮೂಲ್ಯ." },
    { top:"A piece of India",           accent:"on your wrist." },
    { top:"ತಾತನ ಗಡಿಯಾರ",              accent:"ಒಂದು ಕಥೆ ಹೇಳುತ್ತದೆ." },
    { top:"ನಾನನ Ricoh",               accent:"ಮತ್ತೆ ಹುಡುಕಿ." },
    { top:"ಅತ್ತೆ ಧರಿಸಿದ ಗಡಿಯಾರ",     accent:"ನಿಮ್ಮ ಮದುವೆಯಲ್ಲಿ." },
    { top:"ಭಾರತದ ಒಂದು ತುಣುಕು",        accent:"ನಿಮ್ಮ ಮಣಿಕಟ್ಟಿನಲ್ಲಿ." },
  ],
};

// ─── UI Strings ───────────────────────────────────────────────────────────────
const S = {
  en: {
    // ── Common ──
    cancel:"Cancel", save:"Save Changes", close:"Close", back_btn:"Back",
    send_btn:"Send", submit_btn:"Submit", apply_btn:"Apply", track_btn:"Track",
    brand_lbl:"Brand", model_lbl:"Model", decade_lbl:"Decade", condition_lbl:"Condition",
    any_lbl:"Any", notes_lbl:"Notes", price_lbl:"Price ₹", email_lbl:"Email",
    name_lbl:"Full Name", phone_lbl:"WhatsApp / Mobile", city_lbl:"City",
    desc_lbl:"Description", photos_lbl:"Photos", filters_lbl:"Filters", clear_btn:"Clear",
    search_ph:"Search brand, model…", use_credit:"Use Credit →",
    pay_upi:"Pay via UPI →", or_lbl:"or", optional_lbl:"(optional)", uploading_lbl:"Uploading…",
    verified_lbl:"Verified", trusted_lbl:"Trusted", pending_lbl:"Pending",
    rating_lbl:"Rating", sales_lbl:"Sales", since_lbl:"Since", no_updates:"No updates yet.",
    no_matches:"No matches found.", enquire_btn:"Enquire →", all_lbl:"All",
    for_sale_lbl:"For Sale", wanted_lbl:"Wanted", movement_lbl:"Movement",
    seller_lbl:"Seller", budget_lbl:"Budget ₹", reference_lbl:"Reference",
    signin_req:"Sign in to continue", track_req:"Track my request →",
    message_seller:"Message to seller", no_order:"No order found. Check the ID.",
    offers_lbl:"SELLER OFFERS", no_offers:"No live offers yet — sellers are being notified.",
    confirm_receipt:"✓ Confirm Receipt", raise_dispute:"⚑ Raise Dispute",
    rate_seller:"★ Rate Seller", chat_btn:"💬 Chat", upload_unboxing:"🎬 Upload Unboxing",
    sourcing_fee:"Sourcing fee", total_lbl:"Total", availability_lbl:"Availability",
    trusted_only:"✦ Trusted sellers only", decade_filter:"Decade",
    authenticated:"✓ Auth", sign_in_btn:"Sign in →", join_btn:"Join free",
    // ── Nav ──
    nav_catalog:"Explore", nav_market:"Sell", nav_findtrack:"Find & Track",
    nav_chat:"Chat", nav_auth:"Authenticate", nav_services:"Services",
    nav_stories:"Stories", nav_labs:"Labs", nav_wotd:"WOTD",
    nav_signin:"Sign in", nav_signup:"Join",
    hero_browse:"Browse Catalog →", hero_find:"Find a watch — ₹99",
    hero_signin:"Sign in",
    hero_sub:"India's platform for vintage watch collectors. Buy, sell, and authenticate — trusted sellers, community catalog.",
    hero_tag1:"◉ Thousands of vintage watches", hero_tag2:"✦ Trusted seller network", hero_tag3:"◎ Collector haven",
    stat_sourced:"watches sourced", stat_catalog:"catalog entries",
    sec_catalog:"The Catalog", sec_catalog_h:"Watches worth remembering.",
    browse_all:"Browse all →", sec_services:"Services", sec_services_h:"How we help.",
    sec_process:"Process", sec_process_h:"How sourcing works.",
    sec_sellers:"Sellers", sec_sellers_h:"Trusted sellers.", view_all:"View all →",
    svc_find:"Find a Watch", svc_find_sub:"Describe it. We source it.", svc_find_p:"₹99",
    svc_auth:"Authenticate", svc_auth_sub:"Certify what you bought.", svc_auth_p:"₹300",
    svc_market:"Buy & Sell", svc_market_sub:"Marketplace of trusted sellers.", svc_market_p:"Free",
    svc_service:"Watch Servicing", svc_service_sub:"Expert care for vintage pieces.", svc_service_p:"from ₹200",
    step1_h:"Submit your request", step1_b:"Tell us what watch you're looking for — model, decade, condition, budget. Takes 2 minutes.",
    step2_h:"We search our network", step2_b:"Our verified sellers scour their inventory and networks across India.",
    step3_h:"Get matched", step3_b:"When a seller has your watch, you receive a notification with photos and price.",
    list_watch:"List a Watch",
    // ── Market ──
    mkt_head:"Trusted Seller Marketplace", mkt_sub:"Buy from people who actually know.",
    mkt_tab_sale:"FOR SALE", mkt_tab_list:"List a Watch", mkt_tab_sellers:"Sellers", mkt_tab_apply:"Become a Seller",
    mkt_filter_trusted:"Trusted only", mkt_send_enq:"Send enquiry →",
    mkt_enq_ph:"Questions about condition, provenance, original parts…",
    mkt_all_sellers:"All Sellers", mkt_apply_sell:"Apply to Sell →",
    mkt_model_ph:"Pilot, Stride…", mkt_desc_ph:"Be honest. Dial, case, movement. What's original, what's not.",
    mkt_submit_listing:"Submit listing →", mkt_signin_list:"Sign in to list a watch",
    mkt_review:"Application Under Review", mkt_apply_needed:"Seller account required",
    mkt_zero_fee:"Zero listing fee", mkt_badge:"Trusted seller badge",
    mkt_demand:"Sourcing demand alerts", mkt_storefront:"Your own storefront",
    mkt_enq_direct:"Direct buyer enquiries", mkt_dispute:"Admin-backed disputes",
    mkt_trusted_sellers:"Trusted Sellers", mkt_avg_rating:"Avg Rating",
    mkt_verified_listings:"Verified Listings",
    mkt_enq_done_h:"Enquiry sent.", mkt_watch_photos:"Watch photos",
    mkt_signin_apply:"Sign in to apply", mkt_handle_lbl:"Handle",
    mkt_bio_lbl:"About you", mkt_gov_id_lbl:"Government ID",
    mkt_submit_app:"Submit application →", mkt_checking_id:"Checking ID…",
    mkt_app_done_h:"Application in.", cat_suggest_price:"Suggest a price →",
    // ── Request ──
    req_sl:"Find a Watch", req_head:"Tell us what you're looking for.",
    req_sub2:"₹99 · 7–14 days · We tap our seller network so you don't have to.",
    req_model_ph:"Pilot, Janata, Stride…",
    req_notes_ph:"Original crown? Specific dial colour? Exhibition caseback? Any detail helps.",
    req_photos_lbl:"Reference Photos", req_add_auth:"Add Authenticity Check",
    req_sourcing_fee:"Sourcing fee", req_total:"Total",
    req_your_reqs:"Your Requests", req_lookup:"LOOK UP BY ORDER ID",
    req_lookup_ph:"THC-XXXXX", req_no_reqs:"You haven't placed any requests yet.",
    req_new_btn:"+ Request Another Watch", req_close_form:"✕ Close form",
    req_no_order:"No order found. Check the ID.",
    // ── Track ──
    trk_sl:"Order Tracking", trk_head:"Your requests.",
    trk_signin_h:"Sign in to see your requests",
    trk_signin_b:"Track every order, see seller offers, confirm delivery.",
    trk_lookup:"LOOK UP BY ORDER ID", trk_lookup_ph:"THC-XXXXX or AUTH-XXXXX",
    trk_no_reqs:"You haven't placed any requests yet.",
    trk_credit_lbl:"ACCOUNT CREDIT", trk_credit_sub:"Apply towards your next watch search",
    trk_history:"History", trk_hide:"Hide", trk_edit:"✏️ Edit Request",
    trk_save:"Save Changes", trk_cancel_edit:"Cancel", trk_notes_ph:"Describe what you're looking for…",
    // ── Catalog ──
    cat_sl:"Explore", cat_head:"Browse & discover.",
    cat_search_ph:"Search brand, model…", cat_avail:"For sale now",
    cat_all_watches:"All watches", cat_want:"I want this →",
    cat_source:"Source this →", cat_make_offer:"Make offer →",
    cat_no_entries:"No entries yet. Collectors are building the catalog.",
    cat_no_matches:"No matches found.",
    cat_no_sale:"No watches for sale right now. Try removing the Available filter.",
    cat_req_success_h:"Request submitted.",
    cat_req_success_b:"We're contacting sellers right now. You'll hear back within 2–3 days.",
    cat_req_track:"Track my request →", cat_back_browse:"Back to browsing",
    cat_offer_success_h:"Offer sent!", cat_offer_success_b:"Head to chat — the seller will respond there.",
    cat_go_chat:"Go to Chat →", cat_offer_lbl:"Your offer (₹)", cat_send_offer:"Send Offer →",
    cat_authentic:"Authentic", cat_aftermarket:"Aftermarket", cat_unverified:"Unverified",
    // ── Auth service ──
    auth_sl:"Authenticity Service", auth_head:"Real or not? We'll tell you.",
    auth_sub2:"Upload photos, we examine everything. Certificate in 3–5 days. ₹300.",
    auth_watch_lbl:"Watch *", auth_watch_ph:"HMT Pilot black dial · Allwyn Challenge · be specific",
    auth_doubts_lbl:"Doubts / backstory", auth_doubts_ph:"Where from? What's suspicious? Serial number?",
    auth_photos_lbl:"Photos (up to 6 — dial, caseback, movement)",
    auth_submit_btn:"Pay ₹300 & Submit →",
    auth_success_h:"Request logged.", auth_success_b:"We'll review and issue certificate in 3–5 days.",
    // ── Services ──
    svc_head2:"Your vintage watch deserves expert hands.",
    svc_sub2:"In-house watchmaker in Jaipur. Nationwide service via insured courier.",
    svc_book:"BOOK →", svc_submit:"Submit request →",
    svc_name_lbl:"Your name *", svc_city_lbl:"City *",
    svc_watch_brand:"Watch Brand *", svc_watch_model:"Watch Model / Reference",
    svc_issue_lbl:"Describe the issue or work needed",
    svc_pay_note:"No advance · Pay on completion via UPI",
    svc_success_h:"Request received", svc_success_b:"We'll reach out on WhatsApp within 24 hours.",
    svc_courier:"📦 Courier in / out", svc_dropoff:"🏪 Drop off in Jaipur",
    svc_how_works:"How courier service works", svc_no_advance:"No advance payment",
    svc_pricing:"Services & pricing", svc_what_offer:"What we offer.",
    svc_straps:"Straps & Accessories", svc_chat:"Chat with us →",
    svc_join:"Are you a watchmaker or vendor?", svc_apply:"Apply to join →",
    // ── Labs ──
    labs_sl:"Watch Labs · Beta", labs_head:"Design watches. Make them real.",
    labs_sub:"Submit your dream watch design. If 10 collectors pre-order it, we'll put it into production.",
    labs_sellers_only:"Open to sellers and collectors only.",
    // ── Stories ──
    stories_community:"Community", stories_head:"Watch Stories",
    stories_sub:"Personal histories, family heirlooms, restoration journeys — the stories behind vintage Indian watches.",
    stories_loading:"Loading stories…",
    // ── WOTD ──
    wotd_daily:"Daily Wall", wotd_head:"Watch of the Day",
  },
  hi: {
    // ── Common ──
    cancel:"रद्द करें", save:"बदलाव सहेजें", close:"बंद करें", back_btn:"वापस",
    send_btn:"भेजें", submit_btn:"जमा करें", apply_btn:"आवेदन करें", track_btn:"ट्रैक करें",
    brand_lbl:"ब्रांड", model_lbl:"मॉडल", decade_lbl:"दशक", condition_lbl:"स्थिति",
    any_lbl:"कोई भी", notes_lbl:"नोट्स", price_lbl:"कीमत ₹", email_lbl:"ईमेल",
    name_lbl:"पूरा नाम", phone_lbl:"WhatsApp / मोबाइल", city_lbl:"शहर",
    desc_lbl:"विवरण", photos_lbl:"फ़ोटो", filters_lbl:"फ़िल्टर", clear_btn:"साफ़ करें",
    search_ph:"ब्रांड, मॉडल खोजें…", use_credit:"क्रेडिट उपयोग करें →",
    pay_upi:"UPI से भुगतान करें →", or_lbl:"या", optional_lbl:"(वैकल्पिक)", uploading_lbl:"अपलोड हो रहा है…",
    verified_lbl:"सत्यापित", trusted_lbl:"विश्वसनीय", pending_lbl:"लंबित",
    rating_lbl:"रेटिंग", sales_lbl:"बिक्री", since_lbl:"से", no_updates:"अभी कोई अपडेट नहीं।",
    no_matches:"कोई परिणाम नहीं मिला।", enquire_btn:"पूछें →", all_lbl:"सभी",
    for_sale_lbl:"बिक्री के लिए", wanted_lbl:"चाहिए", movement_lbl:"मूवमेंट",
    seller_lbl:"विक्रेता", budget_lbl:"बजट ₹", reference_lbl:"संदर्भ",
    signin_req:"जारी रखने के लिए साइन इन करें", track_req:"अनुरोध ट्रैक करें →",
    message_seller:"विक्रेता को संदेश", no_order:"ऑर्डर नहीं मिला। ID जाँचें।",
    offers_lbl:"विक्रेता ऑफ़र", no_offers:"अभी कोई ऑफ़र नहीं — विक्रेताओं को सूचित किया जा रहा है।",
    confirm_receipt:"✓ रसीद की पुष्टि", raise_dispute:"⚑ विवाद उठाएं",
    rate_seller:"★ विक्रेता को रेट करें", chat_btn:"💬 चैट", upload_unboxing:"🎬 अनबॉक्सिंग अपलोड",
    sourcing_fee:"सोर्सिंग शुल्क", total_lbl:"कुल", availability_lbl:"उपलब्धता",
    trusted_only:"✦ केवल विश्वसनीय", decade_filter:"दशक",
    authenticated:"✓ प्रमाणित", sign_in_btn:"साइन इन →", join_btn:"मुफ़्त जुड़ें",
    nav_catalog:"एक्सप्लोर", nav_market:"बेचें", nav_findtrack:"खोजें व ट्रैक",
    nav_chat:"चैट", nav_auth:"प्रमाणित करें", nav_services:"सेवाएँ",
    nav_stories:"कहानियाँ", nav_labs:"लैब्स", nav_wotd:"WOTD",
    nav_signin:"साइन इन", nav_signup:"जुड़ें",
    hero_browse:"कैटलॉग देखें →", hero_find:"घड़ी खोजें — ₹99",
    hero_signin:"साइन इन",
    hero_sub:"विंटेज घड़ियों के लिए भारत का प्लेटफ़ॉर्म। खरीदें, बेचें, प्रमाणित करें — विश्वसनीय विक्रेता, समुदाय कैटलॉग।",
    hero_tag1:"◉ हज़ारों विंटेज घड़ियाँ", hero_tag2:"✦ विश्वसनीय विक्रेता नेटवर्क", hero_tag3:"◎ संग्राहकों का घर",
    stat_sourced:"घड़ियाँ मिलीं", stat_catalog:"कैटलॉग एंट्री",
    sec_catalog:"कैटलॉग", sec_catalog_h:"यादगार घड़ियाँ।",
    browse_all:"सभी देखें →", sec_services:"सेवाएँ", sec_services_h:"हम कैसे मदद करते हैं।",
    sec_process:"प्रक्रिया", sec_process_h:"सोर्सिंग कैसे होती है।",
    sec_sellers:"विक्रेता", sec_sellers_h:"विश्वसनीय विक्रेता।", view_all:"सभी देखें →",
    svc_find:"घड़ी खोजें", svc_find_sub:"बताएं। हम ढूंढते हैं।", svc_find_p:"₹99",
    svc_auth:"प्रमाणित करें", svc_auth_sub:"जो खरीदा, उसे प्रमाणित करें।", svc_auth_p:"₹300",
    svc_market:"खरीदें व बेचें", svc_market_sub:"विश्वसनीय विक्रेताओं का बाज़ार।", svc_market_p:"मुफ़्त",
    svc_service:"घड़ी सर्विसिंग", svc_service_sub:"विंटेज घड़ियों की विशेषज्ञ देखभाल।", svc_service_p:"₹200 से",
    step1_h:"अपना अनुरोध भेजें", step1_b:"बताएं कि आप कौन सी घड़ी ढूंढ रहे हैं — मॉडल, दशक, स्थिति, बजट। 2 मिनट लगते हैं।",
    step2_h:"हम नेटवर्क में खोजते हैं", step2_b:"हमारे सत्यापित विक्रेता पूरे भारत में अपनी इन्वेंटरी और नेटवर्क में खोजते हैं।",
    step3_h:"मिलान होता है", step3_b:"जब किसी विक्रेता के पास आपकी घड़ी होती है, तो आपको फ़ोटो और कीमत के साथ सूचना मिलती है।",
    list_watch:"घड़ी बेचें",
    // ── Market ──
    mkt_head:"विश्वसनीय विक्रेता बाज़ार", mkt_sub:"सच में जानकारों से खरीदें।",
    mkt_tab_sale:"बिक्री के लिए", mkt_tab_list:"घड़ी बेचें", mkt_tab_sellers:"विक्रेता", mkt_tab_apply:"विक्रेता बनें",
    mkt_filter_trusted:"केवल विश्वसनीय", mkt_send_enq:"पूछताछ भेजें →",
    mkt_all_sellers:"सभी विक्रेता", mkt_apply_sell:"बेचने के लिए आवेदन →",
    mkt_submit_listing:"लिस्टिंग सबमिट करें →", mkt_signin_list:"घड़ी बेचने के लिए साइन इन करें",
    mkt_review:"आवेदन समीक्षाधीन है", mkt_apply_needed:"विक्रेता खाता चाहिए",
    mkt_trusted_sellers:"विश्वसनीय विक्रेता", mkt_avg_rating:"औसत रेटिंग", mkt_verified_listings:"सत्यापित लिस्टिंग",
    mkt_enq_done_h:"पूछताछ भेजी।", mkt_watch_photos:"घड़ी की फ़ोटो",
    mkt_signin_apply:"आवेदन के लिए साइन इन करें", mkt_handle_lbl:"हैंडल",
    mkt_bio_lbl:"अपने बारे में", mkt_gov_id_lbl:"सरकारी पहचान पत्र",
    mkt_submit_app:"आवेदन सबमिट करें →", mkt_checking_id:"ID जाँची जा रही है…",
    mkt_app_done_h:"आवेदन मिला।", cat_suggest_price:"कीमत सुझाएं →",
    // ── Request ──
    req_sl:"घड़ी खोजें", req_head:"बताएं आप क्या ढूंढ रहे हैं।",
    req_sub2:"₹99 · 7–14 दिन · हम विक्रेता नेटवर्क से खोजते हैं।",
    req_your_reqs:"आपके अनुरोध", req_no_reqs:"अभी तक कोई अनुरोध नहीं।",
    req_new_btn:"+ एक और घड़ी खोजें", req_close_form:"✕ फ़ॉर्म बंद करें",
    req_lookup:"ORDER ID से देखें", req_lookup_ph:"THC-XXXXX",
    // ── Track ──
    trk_sl:"ऑर्डर ट्रैकिंग", trk_head:"आपके अनुरोध।",
    trk_signin_h:"अनुरोध देखने के लिए साइन इन करें",
    trk_signin_b:"हर ऑर्डर ट्रैक करें, विक्रेता ऑफ़र देखें, डिलीवरी कन्फ़र्म करें।",
    trk_no_reqs:"अभी तक कोई अनुरोध नहीं।",
    trk_lookup:"ORDER ID से देखें", trk_lookup_ph:"THC-XXXXX या AUTH-XXXXX",
    trk_history:"इतिहास", trk_hide:"छुपाएं", trk_edit:"✏️ अनुरोध संपादित करें",
    // ── Catalog ──
    cat_sl:"एक्सप्लोर", cat_head:"ब्राउज़ करें और खोजें।",
    cat_avail:"अभी बिक्री के लिए", cat_all_watches:"सभी घड़ियाँ",
    cat_want:"मुझे यह चाहिए →", cat_source:"यह खोजें →", cat_make_offer:"ऑफ़र करें →",
    cat_no_entries:"अभी कोई एंट्री नहीं।", cat_no_matches:"कोई परिणाम नहीं।",
    cat_no_sale:"अभी बिक्री के लिए कोई घड़ी नहीं।",
    cat_req_success_h:"अनुरोध सबमिट हुआ।", cat_req_success_b:"हम अभी विक्रेताओं से संपर्क कर रहे हैं। 2–3 दिनों में उत्तर मिलेगा।",
    cat_req_track:"मेरा अनुरोध ट्रैक करें →", cat_back_browse:"वापस ब्राउज़ करें",
    cat_offer_success_h:"ऑफ़र भेजा!", cat_offer_success_b:"चैट में जाएं — विक्रेता वहाँ जवाब देगा।",
    cat_go_chat:"चैट पर जाएं →", cat_offer_lbl:"आपका ऑफ़र (₹)", cat_send_offer:"ऑफ़र भेजें →",
    cat_authentic:"असली", cat_aftermarket:"आफ़्टरमार्केट", cat_unverified:"अज्ञात",
    // ── Auth service ──
    auth_sl:"प्रमाणिकता सेवा", auth_head:"असली या नकली? हम बताएंगे।",
    auth_sub2:"फ़ोटो अपलोड करें, हम सब जाँचते हैं। 3–5 दिनों में सर्टिफ़िकेट। ₹300।",
    auth_watch_lbl:"घड़ी *", auth_watch_ph:"HMT Pilot काली डायल · Allwyn Challenge · विस्तार से बताएं",
    auth_doubts_lbl:"शंकाएं / पृष्ठभूमि", auth_doubts_ph:"कहाँ से? क्या संदिग्ध? सीरियल नंबर?",
    auth_photos_lbl:"फ़ोटो (6 तक — डायल, केसबैक, मूवमेंट)",
    auth_submit_btn:"₹300 दें और सबमिट करें →",
    auth_success_h:"अनुरोध दर्ज।", auth_success_b:"हम 3–5 दिनों में समीक्षा कर सर्टिफ़िकेट जारी करेंगे।",
    // ── Services ──
    svc_head2:"आपकी विंटेज घड़ी को विशेषज्ञ हाथ चाहिए।",
    svc_sub2:"जयपुर में इन-हाउस घड़ीसाज़। बीमाकृत कूरियर द्वारा राष्ट्रव्यापी सेवा।",
    svc_book:"बुक करें →", svc_submit:"अनुरोध करें →",
    svc_success_h:"अनुरोध प्राप्त हुआ", svc_success_b:"हम 24 घंटों में WhatsApp पर संपर्क करेंगे।",
    svc_courier:"📦 कूरियर इन/आउट", svc_dropoff:"🏪 जयपुर में ड्रॉप-ऑफ़",
    labs_sl:"वॉच लैब्स · बीटा", labs_head:"घड़ियाँ डिज़ाइन करें। उन्हें असली बनाएं।",
    labs_sub:"अपने सपने की घड़ी सबमिट करें। 10 कलेक्टर प्री-ऑर्डर करें तो प्रोडक्शन शुरू होगा।",
    labs_sellers_only:"केवल विक्रेताओं और संग्रहकर्ताओं के लिए।",
    stories_community:"समुदाय", stories_head:"घड़ियों की कहानियाँ",
    stories_sub:"निजी इतिहास, परिवार की विरासत, पुनर्स्थापना की यात्राएं — विंटेज भारतीय घड़ियों के पीछे की कहानियाँ।",
    stories_loading:"कहानियाँ लोड हो रही हैं…",
    wotd_daily:"दैनिक दीवार", wotd_head:"आज की घड़ी",
  },
  mr: {
    // ── Common ──
    cancel:"रद्द करा", save:"बदल जतन करा", close:"बंद करा", back_btn:"मागे",
    send_btn:"पाठवा", submit_btn:"सबमिट करा", apply_btn:"अर्ज करा", track_btn:"ट्रॅक करा",
    brand_lbl:"ब्रँड", model_lbl:"मॉडेल", decade_lbl:"दशक", condition_lbl:"स्थिती",
    any_lbl:"कोणतेही", notes_lbl:"नोट्स", price_lbl:"किंमत ₹", email_lbl:"ईमेल",
    name_lbl:"पूर्ण नाव", phone_lbl:"WhatsApp / मोबाइल", city_lbl:"शहर",
    desc_lbl:"वर्णन", photos_lbl:"फोटो", filters_lbl:"फिल्टर", clear_btn:"साफ करा",
    search_ph:"ब्रँड, मॉडेल शोधा…", use_credit:"क्रेडिट वापरा →",
    pay_upi:"UPI ने पेमेंट →", or_lbl:"किंवा", optional_lbl:"(पर्यायी)", uploading_lbl:"अपलोड होत आहे…",
    verified_lbl:"सत्यापित", trusted_lbl:"विश्वसनीय", pending_lbl:"प्रलंबित",
    rating_lbl:"रेटिंग", sales_lbl:"विक्री", since_lbl:"पासून", no_updates:"अजून अपडेट नाही.",
    no_matches:"कोणतेही परिणाम नाही.", enquire_btn:"चौकशी →", all_lbl:"सर्व",
    for_sale_lbl:"विक्रीसाठी", wanted_lbl:"हवे आहे", movement_lbl:"मूव्हमेंट",
    seller_lbl:"विक्रेता", budget_lbl:"बजेट ₹", reference_lbl:"संदर्भ",
    signin_req:"पुढे जाण्यासाठी साइन इन करा", track_req:"माझी विनंती ट्रॅक करा →",
    no_order:"ऑर्डर सापडला नाही. ID तपासा.",
    offers_lbl:"विक्रेत्याचे ऑफर", no_offers:"अजून ऑफर नाही — विक्रेत्यांना सूचित करत आहे.",
    confirm_receipt:"✓ पावती पुष्टी करा", raise_dispute:"⚑ तक्रार दाखल करा",
    rate_seller:"★ विक्रेत्याला रेट करा", chat_btn:"💬 चॅट", upload_unboxing:"🎬 अनबॉक्सिंग अपलोड",
    sourcing_fee:"सोर्सिंग शुल्क", total_lbl:"एकूण", availability_lbl:"उपलब्धता",
    trusted_only:"✦ केवळ विश्वसनीय", decade_filter:"दशक",
    authenticated:"✓ प्रमाणित", sign_in_btn:"साइन इन →", join_btn:"मोफत सामील व्हा",
    // ── Market ──
    mkt_head:"विश्वसनीय विक्रेत्यांचे बाजार", mkt_sub:"खरोखर जाणकार लोकांकडून खरेदी करा.",
    mkt_tab_sale:"विक्रीसाठी", mkt_tab_list:"घड्याळ विका", mkt_tab_sellers:"विक्रेते", mkt_tab_apply:"विक्रेता व्हा",
    mkt_filter_trusted:"केवळ विश्वसनीय", mkt_send_enq:"चौकशी पाठवा →",
    mkt_all_sellers:"सर्व विक्रेते", mkt_apply_sell:"विक्रीसाठी अर्ज करा →",
    mkt_submit_listing:"लिस्टिंग सबमिट करा →", mkt_signin_list:"घड्याळ विकण्यासाठी साइन इन करा",
    mkt_trusted_sellers:"विश्वसनीय विक्रेते", mkt_avg_rating:"सरासरी रेटिंग", mkt_verified_listings:"सत्यापित लिस्टिंग",
    mkt_enq_done_h:"चौकशी पाठवली.", mkt_watch_photos:"घड्याळाचे फोटो",
    mkt_signin_apply:"अर्ज करण्यासाठी साइन इन करा", mkt_handle_lbl:"हँडल",
    mkt_bio_lbl:"तुमच्याबद्दल", mkt_gov_id_lbl:"सरकारी ओळखपत्र",
    mkt_submit_app:"अर्ज सबमिट करा →", mkt_checking_id:"ID तपासत आहे…",
    mkt_app_done_h:"अर्ज प्राप्त झाला.", cat_suggest_price:"किंमत सुचवा →",
    // ── Request ──
    req_sl:"घड्याळ शोधा", req_head:"तुम्हाला काय हवे ते सांगा.",
    req_sub2:"₹99 · 7–14 दिवस · आम्ही विक्रेत्यांशी संपर्क करतो.",
    req_your_reqs:"तुमच्या विनंत्या", req_no_reqs:"अद्याप कोणत्याही विनंत्या नाहीत.",
    req_new_btn:"+ आणखी एक घड्याळ मागवा",
    // ── Track ──
    trk_sl:"ऑर्डर ट्रॅकिंग", trk_head:"तुमच्या विनंत्या.",
    trk_signin_h:"विनंत्या पाहण्यासाठी साइन इन करा",
    trk_no_reqs:"अद्याप कोणत्याही विनंत्या नाहीत.",
    // ── Catalog ──
    cat_sl:"एक्सप्लोर", cat_head:"शोधा आणि जाणा.",
    cat_avail:"आता विक्रीसाठी", cat_all_watches:"सर्व घड्याळे",
    cat_want:"मला हे हवे →", cat_source:"हे सोर्स करा →",
    cat_no_entries:"अद्याप नोंदी नाहीत.", cat_no_matches:"कोणतेही परिणाम नाही.",
    // ── Auth service ──
    auth_sl:"सत्यता सेवा", auth_head:"खरे आहे का? आम्ही सांगतो.",
    auth_submit_btn:"₹300 भरा आणि सबमिट करा →",
    auth_success_h:"विनंती नोंदवली.", auth_success_b:"आम्ही 3–5 दिवसांत प्रमाणपत्र जारी करू.",
    // ── Services ──
    svc_head2:"तुमच्या विंटेज घड्याळाला तज्ञ हात हवेत.",
    svc_book:"बुक करा →", svc_submit:"विनंती सबमिट करा →",
    svc_success_h:"विनंती प्राप्त झाली", svc_success_b:"आम्ही 24 तासांत WhatsApp वर संपर्क करू.",
    nav_catalog:"एक्सप्लोर", nav_market:"विका", nav_findtrack:"शोधा व ट्रॅक",
    nav_chat:"चॅट", nav_auth:"प्रमाणित करा", nav_services:"सेवा",
    nav_stories:"कथा", nav_labs:"लॅब्स", nav_wotd:"WOTD",
    nav_signin:"साइन इन", nav_signup:"सामील व्हा",
    hero_browse:"कॅटलॉग पाहा →", hero_find:"घड्याळ शोधा — ₹99",
    hero_signin:"साइन इन",
    hero_sub:"विंटेज घड्याळांसाठी भारताचे व्यासपीठ। विका, खरेदी करा, प्रमाणित करा — विश्वसनीय विक्रेते, समुदाय कॅटलॉग।",
    hero_tag1:"◉ हजारो विंटेज घड्याळे", hero_tag2:"✦ विश्वसनीय विक्रेता नेटवर्क", hero_tag3:"◎ संग्राहकांचे घर",
    stat_sourced:"घड्याळे मिळाली", stat_catalog:"कॅटलॉग एंट्री",
    sec_catalog:"कॅटलॉग", sec_catalog_h:"लक्षात राहण्याजोग्या घड्याळे।",
    browse_all:"सर्व पाहा →", sec_services:"सेवा", sec_services_h:"आम्ही कसे मदत करतो।",
    sec_process:"प्रक्रिया", sec_process_h:"सोर्सिंग कशी होते।",
    sec_sellers:"विक्रेते", sec_sellers_h:"विश्वसनीय विक्रेते।", view_all:"सर्व पाहा →",
    svc_find:"घड्याळ शोधा", svc_find_sub:"सांगा। आम्ही शोधतो।", svc_find_p:"₹99",
    svc_auth:"प्रमाणित करा", svc_auth_sub:"जे खरेदी केले ते प्रमाणित करा।", svc_auth_p:"₹300",
    svc_market:"खरेदी व विक्री", svc_market_sub:"विश्वसनीय विक्रेत्यांचा बाजार।", svc_market_p:"मोफत",
    svc_service:"घड्याळ सर्व्हिसिंग", svc_service_sub:"विंटेज घड्याळांची तज्ञ काळजी।", svc_service_p:"₹200 पासून",
    step1_h:"तुमची विनंती पाठवा", step1_b:"तुम्हाला कोणते घड्याळ हवे ते सांगा — मॉडेल, दशक, स्थिती, बजेट। 2 मिनिटे लागतात।",
    step2_h:"आम्ही नेटवर्कमध्ये शोधतो", step2_b:"आमचे सत्यापित विक्रेते संपूर्ण भारतात शोधतात।",
    step3_h:"जुळणी होते", step3_b:"जेव्हा एखाद्या विक्रेत्याकडे तुमचे घड्याळ असते, तेव्हा तुम्हाला फोटो व किंमतीसह सूचना मिळते।",
    list_watch:"घड्याळ विका",
  },
  gu: {
    // ── Common ──
    cancel:"રદ કરો", save:"ફેરફારો સાચવો", close:"બંધ કરો", back_btn:"પાછળ",
    send_btn:"મોકલો", submit_btn:"સબમિટ કરો", apply_btn:"અરજી કરો", track_btn:"ટ્રૅક કરો",
    brand_lbl:"બ્રૅન્ડ", model_lbl:"મૉડેલ", decade_lbl:"દાયકો", condition_lbl:"સ્થિતિ",
    any_lbl:"કોઈ પણ", notes_lbl:"નોંધ", price_lbl:"કિંમત ₹", email_lbl:"ઈ-મેઇલ",
    name_lbl:"પૂરું નામ", phone_lbl:"WhatsApp / મોબાઇલ", city_lbl:"શહેર",
    desc_lbl:"વર્ણન", photos_lbl:"ફોટો", filters_lbl:"ફિલ્ટર", clear_btn:"સાફ કરો",
    search_ph:"બ્રૅન્ડ, મૉડેલ શોધો…", use_credit:"ક્રેડિટ વાપરો →",
    pay_upi:"UPI થી ચૂકવો →", or_lbl:"અથવા", optional_lbl:"(વૈકલ્પિક)", uploading_lbl:"અપલોડ થઈ રહ્યું છે…",
    verified_lbl:"ચકાસાયેલ", trusted_lbl:"વિશ્વસનીય", pending_lbl:"બાકી",
    rating_lbl:"રેટિંગ", sales_lbl:"વેચાણ", since_lbl:"થી", no_updates:"હજી કોઈ અપડેટ નથી.",
    no_matches:"કોઈ પરિણામ નથી.", enquire_btn:"પૂછો →", all_lbl:"બધા",
    for_sale_lbl:"વેચાણ માટે", wanted_lbl:"જોઈએ છે", movement_lbl:"મૂવમેન્ટ",
    seller_lbl:"વિક્રેતા", budget_lbl:"બજેટ ₹", reference_lbl:"સંદર્ભ",
    signin_req:"આગળ જવા સાઇન ઇન કરો", track_req:"મારી વિનંતી ટ્રૅક કરો →",
    no_order:"ઑર્ડર મળ્યો નહિ. ID તપાસો.",
    offers_lbl:"વિક્રેતાની ઑફર", no_offers:"હજી ઑફર નથી — વિક્રેતાઓને જાણ કરવામાં આવી રહી છે.",
    confirm_receipt:"✓ રસીદ પ્રાપ્ત", raise_dispute:"⚑ ફરિયાદ",
    rate_seller:"★ વિક્રેતાને રેટ કરો", chat_btn:"💬 ચેટ", upload_unboxing:"🎬 અનબૉક્સિંગ",
    sourcing_fee:"સોર્સિંગ ફી", total_lbl:"કુલ", availability_lbl:"ઉપલબ્ધતા",
    trusted_only:"✦ ફક્ત વિશ્વસનીય", decade_filter:"દાયકો",
    authenticated:"✓ પ્રમાણિત", sign_in_btn:"સાઇન ઇન →", join_btn:"મફત જોડાઓ",
    // ── Market ──
    mkt_head:"વિশ્વસનીય વિક્રેતાઓનો બજાર", mkt_sub:"સાચા જાણકારો પાસેથી ખરીદો.",
    mkt_tab_sale:"વેચાણ માટે", mkt_tab_list:"ઘડિયાળ વેચો", mkt_tab_sellers:"વિક્રેતા", mkt_tab_apply:"વિક્રેતા બનો",
    mkt_filter_trusted:"ફક્ત વિશ્વસનીય", mkt_send_enq:"પૂછપરછ →",
    mkt_all_sellers:"બધા વિક્રેતા", mkt_apply_sell:"વેચવા અરજી →",
    mkt_submit_listing:"લિસ્ટિંગ સબમિટ →", mkt_signin_list:"ઘડિયાળ વેચવા સાઇન ઇન",
    mkt_trusted_sellers:"વિશ્વસનીય વિક્રેતા", mkt_avg_rating:"સ. રેટિંગ", mkt_verified_listings:"ચકાસાયેલ",
    mkt_enq_done_h:"પૂછપરછ મોકલી.", mkt_watch_photos:"ઘડિયાળ ફોટો",
    mkt_signin_apply:"અરજી માટે સાઇન ઇન કરો", mkt_handle_lbl:"હૅન્ડલ",
    mkt_bio_lbl:"તમારા વિષે", mkt_gov_id_lbl:"સરકારી ઓળખ",
    mkt_submit_app:"અરજી સબમિટ કરો →", mkt_checking_id:"ID ચકાસી રહ્યા…",
    mkt_app_done_h:"અરજી મળી.", cat_suggest_price:"ભાવ સૂચવો →",
    // ── Request ──
    req_sl:"ઘડિયાળ શોધો", req_head:"તમે શું શોધો છો તે જણાવો.",
    req_sub2:"₹99 · 7–14 દિવસ · અમે વિક્રેતાઓ સાથે સંપર્ક કરીએ.",
    req_your_reqs:"તમારી વિનંતીઓ", req_no_reqs:"હજી કોઈ વિનંતી નથી.",
    req_new_btn:"+ બીજી ઘડિયાળ મંગાવો",
    // ── Track ──
    trk_sl:"ઑર્ડર ટ્રૅકિંગ", trk_head:"તમારી વિનંતીઓ.",
    trk_signin_h:"વિનંતીઓ જોવા સાઇન ઇન કરો",
    trk_no_reqs:"હજી કોઈ વિનંતી નથી.",
    // ── Catalog ──
    cat_sl:"એક્સ્પ્લોર", cat_head:"શોધો અને જાણો.",
    cat_avail:"હવે વેચાણ માટે", cat_all_watches:"બધી ઘડિયાળો",
    cat_want:"મને આ જોઈએ →", cat_source:"આ સ્રોત →",
    cat_no_entries:"હજી નોંધ નથી.", cat_no_matches:"કોઈ પરિણામ નથી.",
    // ── Auth service ──
    auth_sl:"પ્રમાણિકતા સેવા", auth_head:"સાચું છે? અમે જણાવીએ.",
    auth_submit_btn:"₹300 ચૂકવો અને સબમિટ →",
    auth_success_h:"વિનંતી નોંધાઈ.", auth_success_b:"અમે 3–5 દિવસમાં પ્રમાણ-પત્ર આપીશું.",
    // ── Services ──
    svc_head2:"તમારી વિન્ટેજ ઘડિયાળ નિષ્ણાત હાથ ઇચ્છે.",
    svc_book:"બૂક →", svc_submit:"વિનંતી →",
    svc_success_h:"વિનંતી મળી", svc_success_b:"24 કલાકમાં WhatsApp પર સંપર્ક.",
    nav_catalog:"એક્સ્પ્લોર", nav_market:"વેચો", nav_findtrack:"શોધો અને ટ્રૅક",
    nav_chat:"ચેટ", nav_auth:"પ્રમાણિત કરો", nav_services:"સેવાઓ",
    nav_stories:"વાર્તાઓ", nav_labs:"લૅબ્સ", nav_wotd:"WOTD",
    nav_signin:"સાઇન ઇન", nav_signup:"જોડાઓ",
    hero_browse:"કૅટેલૉગ જુઓ →", hero_find:"ઘડિયાળ શોધો — ₹99",
    hero_signin:"સાઇન ઇન",
    hero_sub:"વિન્ટેજ ઘડિયાળો માટે ભારતનું પ્લૅટફૉર્મ। ખરીદો, વેચો, પ્રમાણિત કરો — વિશ્વસનીય વિક્રેતા, સામુદાયિક કૅટેલૉગ।",
    hero_tag1:"◉ હજારો વિન્ટેજ ઘડિયાળો", hero_tag2:"✦ વિશ્વસનીય વિક્રેતા નેટવર્ક", hero_tag3:"◎ સંગ્રહકોનું ઘર",
    stat_sourced:"ઘડિયાળો મળ્યા", stat_catalog:"કૅટેલૉગ એન્ટ્રી",
    sec_catalog:"કૅટેલૉગ", sec_catalog_h:"યાદગાર ઘડિયાળો।",
    browse_all:"બધા જુઓ →", sec_services:"સેવાઓ", sec_services_h:"અમે કઈ રીતે મદદ કરીએ।",
    sec_process:"પ્રક્રિયા", sec_process_h:"સોર્સિંગ કઈ રીતે થાય।",
    sec_sellers:"વિક્રેતા", sec_sellers_h:"વિશ્વસનીય વિક્રેતા।", view_all:"બધા જુઓ →",
    svc_find:"ઘડિયાળ શોધો", svc_find_sub:"કહો। અમે શોધીએ।", svc_find_p:"₹99",
    svc_auth:"પ્રમાણિત કરો", svc_auth_sub:"જે ખરીદ્યું તે પ્રમાણિત કરો।", svc_auth_p:"₹300",
    svc_market:"ખરીદો અને વેચો", svc_market_sub:"વિશ્વસનીય વિક્રેતાઓનું બજાર।", svc_market_p:"મફત",
    svc_service:"ઘડિયાળ સર્વિસિંગ", svc_service_sub:"વિન્ટેજ ઘડિયાળોની નિષ્ણાત સંભાળ।", svc_service_p:"₹200 થી",
    step1_h:"તમારી વિનંતી મોકલો", step1_b:"કહો કઈ ઘડિયાળ જોઈએ — મૉડેલ, દાયકો, સ્થિતિ, બજેટ। 2 મિનિટ.",
    step2_h:"અમે નેટવર્કમાં શોધીએ", step2_b:"અમારા ચકાસાયેલ વિક્રેતા સમગ્ર ભારતમાં શોધ કરે.",
    step3_h:"મેળ થાય", step3_b:"જ્યારે વિક્રેતા પાસે ઘડિયાળ હોય ત્યારે ફોટો અને ભાવ સાથે સૂચના મળે.",
    list_watch:"ઘડિયાળ વેચો",
  },
  pa: {
    // ── Common ──
    cancel:"ਰੱਦ ਕਰੋ", save:"ਬਦਲਾਅ ਸੁਰੱਖਿਅਤ", close:"ਬੰਦ ਕਰੋ", back_btn:"ਵਾਪਸ",
    send_btn:"ਭੇਜੋ", submit_btn:"ਜਮ੍ਹਾਂ ਕਰੋ", apply_btn:"ਅਰਜ਼ੀ ਦਿਓ", track_btn:"ਟ੍ਰੈਕ ਕਰੋ",
    brand_lbl:"ਬ੍ਰੈਂਡ", model_lbl:"ਮਾਡਲ", decade_lbl:"ਦਹਾਕਾ", condition_lbl:"ਹਾਲਤ",
    any_lbl:"ਕੋਈ ਵੀ", notes_lbl:"ਨੋਟਸ", price_lbl:"ਕੀਮਤ ₹", email_lbl:"ਈਮੇਲ",
    name_lbl:"ਪੂਰਾ ਨਾਮ", phone_lbl:"WhatsApp / ਮੋਬਾਈਲ", city_lbl:"ਸ਼ਹਿਰ",
    desc_lbl:"ਵੇਰਵਾ", photos_lbl:"ਫੋਟੋ", filters_lbl:"ਫਿਲਟਰ", clear_btn:"ਸਾਫ਼ ਕਰੋ",
    search_ph:"ਬ੍ਰੈਂਡ, ਮਾਡਲ ਲੱਭੋ…", use_credit:"ਕ੍ਰੈਡਿਟ ਵਰਤੋ →",
    pay_upi:"UPI ਨਾਲ ਅਦਾਇਗੀ →", or_lbl:"ਜਾਂ", optional_lbl:"(ਵਿਕਲਪਿਕ)", uploading_lbl:"ਅਪਲੋਡ ਹੋ ਰਿਹਾ…",
    verified_lbl:"ਪ੍ਰਮਾਣਿਤ", trusted_lbl:"ਭਰੋਸੇਮੰਦ", pending_lbl:"ਬਾਕੀ",
    rating_lbl:"ਰੇਟਿੰਗ", sales_lbl:"ਵਿਕਰੀ", since_lbl:"ਤੋਂ", no_updates:"ਕੋਈ ਅਪਡੇਟ ਨਹੀਂ।",
    no_matches:"ਕੋਈ ਨਤੀਜਾ ਨਹੀਂ।", enquire_btn:"ਪੁੱਛੋ →", all_lbl:"ਸਾਰੇ",
    for_sale_lbl:"ਵਿਕਰੀ ਲਈ", wanted_lbl:"ਚਾਹੀਦਾ ਹੈ", movement_lbl:"ਮੂਵਮੈਂਟ",
    seller_lbl:"ਵਿਕ੍ਰੇਤਾ", budget_lbl:"ਬਜਟ ₹", reference_lbl:"ਹਵਾਲਾ",
    signin_req:"ਜਾਰੀ ਰੱਖਣ ਲਈ ਸਾਈਨ ਇਨ ਕਰੋ", track_req:"ਮੇਰੀ ਬੇਨਤੀ ਟ੍ਰੈਕ →",
    no_order:"ਆਰਡਰ ਨਹੀਂ ਮਿਲਿਆ। ID ਜਾਂਚੋ।",
    offers_lbl:"ਵਿਕ੍ਰੇਤਾ ਦੀਆਂ ਪੇਸ਼ਕਸ਼ਾਂ", no_offers:"ਕੋਈ ਪੇਸ਼ਕਸ਼ ਨਹੀਂ — ਵਿਕ੍ਰੇਤਿਆਂ ਨੂੰ ਸੂਚਿਤ ਕੀਤਾ ਜਾ ਰਿਹਾ।",
    confirm_receipt:"✓ ਪ੍ਰਾਪਤੀ ਦੀ ਪੁਸ਼ਟੀ", raise_dispute:"⚑ ਸ਼ਿਕਾਇਤ",
    rate_seller:"★ ਵਿਕ੍ਰੇਤਾ ਨੂੰ ਰੇਟ ਕਰੋ", chat_btn:"💬 ਚੈਟ", upload_unboxing:"🎬 ਅਨਬੌਕਸਿੰਗ",
    sourcing_fee:"ਸੋਰਸਿੰਗ ਫੀਸ", total_lbl:"ਕੁੱਲ", availability_lbl:"ਉਪਲਬਧਤਾ",
    trusted_only:"✦ ਸਿਰਫ਼ ਭਰੋਸੇਮੰਦ", decade_filter:"ਦਹਾਕਾ",
    authenticated:"✓ ਪ੍ਰਮਾਣਿਤ", sign_in_btn:"ਸਾਈਨ ਇਨ →", join_btn:"ਮੁਫ਼ਤ ਸ਼ਾਮਲ",
    // ── Market ──
    mkt_head:"ਭਰੋਸੇਮੰਦ ਵਿਕ੍ਰੇਤਾ ਬਾਜ਼ਾਰ", mkt_sub:"ਜਾਣਕਾਰ ਲੋਕਾਂ ਤੋਂ ਖਰੀਦੋ।",
    mkt_tab_sale:"ਵਿਕਰੀ ਲਈ", mkt_tab_list:"ਘੜੀ ਵੇਚੋ", mkt_tab_sellers:"ਵਿਕ੍ਰੇਤਾ", mkt_tab_apply:"ਵਿਕ੍ਰੇਤਾ ਬਣੋ",
    mkt_filter_trusted:"ਸਿਰਫ਼ ਭਰੋਸੇਮੰਦ", mkt_send_enq:"ਪੁੱਛਗਿੱਛ →",
    mkt_all_sellers:"ਸਾਰੇ ਵਿਕ੍ਰੇਤਾ", mkt_apply_sell:"ਵੇਚਣ ਲਈ ਅਰਜ਼ੀ →",
    mkt_submit_listing:"ਲਿਸਟਿੰਗ ਸਬਮਿਟ →", mkt_signin_list:"ਘੜੀ ਵੇਚਣ ਲਈ ਸਾਈਨ ਇਨ",
    mkt_trusted_sellers:"ਭਰੋਸੇਮੰਦ ਵਿਕ੍ਰੇਤਾ", mkt_avg_rating:"ਔਸਤ ਰੇਟਿੰਗ", mkt_verified_listings:"ਪ੍ਰਮਾਣਿਤ",
    mkt_enq_done_h:"ਪੁੱਛਗਿੱਛ ਭੇਜੀ।", mkt_watch_photos:"ਘੜੀ ਦੀਆਂ ਫੋਟੋਆਂ",
    mkt_signin_apply:"ਅਰਜ਼ੀ ਲਈ ਸਾਈਨ ਇਨ ਕਰੋ", mkt_handle_lbl:"ਹੈਂਡਲ",
    mkt_bio_lbl:"ਆਪਣੇ ਬਾਰੇ", mkt_gov_id_lbl:"ਸਰਕਾਰੀ ਪਛਾਣ ਪੱਤਰ",
    mkt_submit_app:"ਅਰਜ਼ੀ ਸਬਮਿਟ ਕਰੋ →", mkt_checking_id:"ID ਜਾਂਚ ਹੋ ਰਹੀ ਹੈ…",
    mkt_app_done_h:"ਅਰਜ਼ੀ ਮਿਲੀ।", cat_suggest_price:"ਕੀਮਤ ਸੁਝਾਓ →",
    // ── Request ──
    req_sl:"ਘੜੀ ਲੱਭੋ", req_head:"ਦੱਸੋ ਤੁਸੀਂ ਕੀ ਲੱਭ ਰਹੇ ਹੋ।",
    req_sub2:"₹99 · 7–14 ਦਿਨ · ਅਸੀਂ ਵਿਕ੍ਰੇਤਿਆਂ ਨਾਲ ਸੰਪਰਕ ਕਰਦੇ ਹਾਂ।",
    req_your_reqs:"ਤੁਹਾਡੀਆਂ ਬੇਨਤੀਆਂ", req_no_reqs:"ਕੋਈ ਬੇਨਤੀ ਨਹੀਂ।",
    req_new_btn:"+ ਹੋਰ ਘੜੀ ਮੰਗੋ",
    // ── Track ──
    trk_sl:"ਆਰਡਰ ਟ੍ਰੈਕਿੰਗ", trk_head:"ਤੁਹਾਡੀਆਂ ਬੇਨਤੀਆਂ।",
    trk_signin_h:"ਬੇਨਤੀਆਂ ਦੇਖਣ ਲਈ ਸਾਈਨ ਇਨ ਕਰੋ",
    trk_no_reqs:"ਕੋਈ ਬੇਨਤੀ ਨਹੀਂ।",
    // ── Catalog ──
    cat_sl:"ਐਕਸਪਲੋਰ", cat_head:"ਲੱਭੋ ਅਤੇ ਜਾਣੋ।",
    cat_avail:"ਹੁਣ ਵਿਕਰੀ ਲਈ", cat_all_watches:"ਸਾਰੀਆਂ ਘੜੀਆਂ",
    cat_want:"ਮੈਨੂੰ ਇਹ ਚਾਹੀਦਾ →", cat_source:"ਇਹ ਸੋਰਸ ਕਰੋ →",
    cat_no_entries:"ਕੋਈ ਐਂਟਰੀ ਨਹੀਂ।", cat_no_matches:"ਕੋਈ ਨਤੀਜਾ ਨਹੀਂ।",
    // ── Auth service ──
    auth_sl:"ਅਸਲੀਅਤ ਸੇਵਾ", auth_head:"ਅਸਲੀ ਹੈ? ਅਸੀਂ ਦੱਸਾਂਗੇ।",
    auth_submit_btn:"₹300 ਦੇ ਕੇ ਸਬਮਿਟ →",
    auth_success_h:"ਬੇਨਤੀ ਦਰਜ।", auth_success_b:"3–5 ਦਿਨਾਂ ਵਿੱਚ ਸਰਟੀਫਿਕੇਟ ਮਿਲੇਗਾ।",
    // ── Services ──
    svc_head2:"ਤੁਹਾਡੀ ਵਿੰਟੇਜ ਘੜੀ ਨੂੰ ਮਾਹਰ ਹੱਥ ਚਾਹੀਦੇ ਹਨ।",
    svc_book:"ਬੁੱਕ →", svc_submit:"ਬੇਨਤੀ →",
    svc_success_h:"ਬੇਨਤੀ ਮਿਲੀ", svc_success_b:"24 ਘੰਟਿਆਂ ਵਿੱਚ WhatsApp 'ਤੇ ਸੰਪਰਕ।",
    nav_catalog:"ਐਕਸਪਲੋਰ", nav_market:"ਵੇਚੋ", nav_findtrack:"ਲੱਭੋ ਅਤੇ ਟ੍ਰੈਕ",
    nav_chat:"ਚੈਟ", nav_auth:"ਪ੍ਰਮਾਣਿਤ ਕਰੋ", nav_services:"ਸੇਵਾਵਾਂ",
    nav_stories:"ਕਹਾਣੀਆਂ", nav_labs:"ਲੈਬਜ਼", nav_wotd:"WOTD",
    nav_signin:"ਸਾਈਨ ਇਨ", nav_signup:"ਸ਼ਾਮਲ ਹੋਵੋ",
    hero_browse:"ਕੈਟਲਾਗ ਦੇਖੋ →", hero_find:"ਘੜੀ ਲੱਭੋ — ₹99",
    hero_signin:"ਸਾਈਨ ਇਨ",
    hero_sub:"ਵਿੰਟੇਜ ਘੜੀਆਂ ਲਈ ਭਾਰਤ ਦਾ ਪਲੇਟਫਾਰਮ। ਖਰੀਦੋ, ਵੇਚੋ, ਪ੍ਰਮਾਣਿਤ ਕਰੋ — ਭਰੋਸੇਮੰਦ ਵਿਕ੍ਰੇਤਾ, ਕਮਿਊਨਿਟੀ ਕੈਟਲਾਗ।",
    hero_tag1:"◉ ਹਜ਼ਾਰਾਂ ਵਿੰਟੇਜ ਘੜੀਆਂ", hero_tag2:"✦ ਭਰੋਸੇਮੰਦ ਵਿਕ੍ਰੇਤਾ ਨੈੱਟਵਰਕ", hero_tag3:"◎ ਸੰਗ੍ਰਾਹਕਾਂ ਦਾ ਘਰ",
    stat_sourced:"ਘੜੀਆਂ ਮਿਲੀਆਂ", stat_catalog:"ਕੈਟਲਾਗ ਐਂਟਰੀ",
    sec_catalog:"ਕੈਟਲਾਗ", sec_catalog_h:"ਯਾਦਗਾਰ ਘੜੀਆਂ।",
    browse_all:"ਸਭ ਦੇਖੋ →", sec_services:"ਸੇਵਾਵਾਂ", sec_services_h:"ਅਸੀਂ ਕਿਵੇਂ ਮਦਦ ਕਰਦੇ ਹਾਂ।",
    sec_process:"ਪ੍ਰਕਿਰਿਆ", sec_process_h:"ਸੋਰਸਿੰਗ ਕਿਵੇਂ ਕੰਮ ਕਰਦੀ ਹੈ।",
    sec_sellers:"ਵਿਕ੍ਰੇਤਾ", sec_sellers_h:"ਭਰੋਸੇਮੰਦ ਵਿਕ੍ਰੇਤਾ।", view_all:"ਸਭ ਦੇਖੋ →",
    svc_find:"ਘੜੀ ਲੱਭੋ", svc_find_sub:"ਦੱਸੋ। ਅਸੀਂ ਲੱਭਦੇ ਹਾਂ।", svc_find_p:"₹99",
    svc_auth:"ਪ੍ਰਮਾਣਿਤ ਕਰੋ", svc_auth_sub:"ਜੋ ਖਰੀਦਿਆ ਉਸਨੂੰ ਪ੍ਰਮਾਣਿਤ ਕਰੋ।", svc_auth_p:"₹300",
    svc_market:"ਖਰੀਦੋ ਅਤੇ ਵੇਚੋ", svc_market_sub:"ਭਰੋਸੇਮੰਦ ਵਿਕ੍ਰੇਤਿਆਂ ਦਾ ਬਾਜ਼ਾਰ।", svc_market_p:"ਮੁਫ਼ਤ",
    svc_service:"ਘੜੀ ਸਰਵਿਸਿੰਗ", svc_service_sub:"ਵਿੰਟੇਜ ਘੜੀਆਂ ਦੀ ਮਾਹਰ ਦੇਖਭਾਲ।", svc_service_p:"₹200 ਤੋਂ",
    step1_h:"ਆਪਣੀ ਬੇਨਤੀ ਭੇਜੋ", step1_b:"ਦੱਸੋ ਕਿਹੜੀ ਘੜੀ ਚਾਹੀਦੀ ਹੈ — ਮਾਡਲ, ਦਹਾਕਾ, ਹਾਲਤ, ਬਜਟ। 2 ਮਿੰਟ।",
    step2_h:"ਅਸੀਂ ਨੈੱਟਵਰਕ ਵਿੱਚ ਲੱਭਦੇ ਹਾਂ", step2_b:"ਸਾਡੇ ਪ੍ਰਮਾਣਿਤ ਵਿਕ੍ਰੇਤਾ ਸਾਰੇ ਭਾਰਤ ਵਿੱਚ ਲੱਭਦੇ ਹਨ।",
    step3_h:"ਮਿਲਾਨ ਹੁੰਦਾ ਹੈ", step3_b:"ਜਦੋਂ ਵਿਕ੍ਰੇਤਾ ਕੋਲ ਤੁਹਾਡੀ ਘੜੀ ਹੋਵੇ, ਫੋਟੋ ਅਤੇ ਕੀਮਤ ਨਾਲ ਸੂਚਨਾ ਮਿਲਦੀ ਹੈ।",
    list_watch:"ਘੜੀ ਵੇਚੋ",
  },
  bn: {
    // ── Common ──
    cancel:"বাতিল", save:"পরিবর্তন সংরক্ষণ", close:"বন্ধ করুন", back_btn:"পিছনে",
    send_btn:"পাঠান", submit_btn:"জমা দিন", apply_btn:"আবেদন করুন", track_btn:"ট্র্যাক",
    brand_lbl:"ব্র্যান্ড", model_lbl:"মডেল", decade_lbl:"দশক", condition_lbl:"অবস্থা",
    any_lbl:"যেকোনো", notes_lbl:"নোটস", price_lbl:"দাম ₹", email_lbl:"ইমেইল",
    name_lbl:"পূর্ণ নাম", phone_lbl:"WhatsApp / মোবাইল", city_lbl:"শহর",
    desc_lbl:"বিবরণ", photos_lbl:"ছবি", filters_lbl:"ফিল্টার", clear_btn:"পরিষ্কার",
    search_ph:"ব্র্যান্ড, মডেল খুঁজুন…", use_credit:"ক্রেডিট ব্যবহার →",
    pay_upi:"UPI তে পেমেন্ট →", or_lbl:"বা", optional_lbl:"(ঐচ্ছিক)", uploading_lbl:"আপলোড হচ্ছে…",
    verified_lbl:"যাচাইকৃত", trusted_lbl:"বিশ্বস্ত", pending_lbl:"মুলতুবি",
    rating_lbl:"রেটিং", sales_lbl:"বিক্রয়", since_lbl:"থেকে", no_updates:"এখনো কোনো আপডেট নেই।",
    no_matches:"কোনো ফলাফল নেই।", enquire_btn:"জিজ্ঞেস করুন →", all_lbl:"সব",
    for_sale_lbl:"বিক্রয়ের জন্য", wanted_lbl:"চাই", movement_lbl:"মুভমেন্ট",
    seller_lbl:"বিক্রেতা", budget_lbl:"বাজেট ₹", reference_lbl:"রেফারেন্স",
    signin_req:"চালিয়ে যেতে সাইন ইন করুন", track_req:"আমার অনুরোধ ট্র্যাক →",
    no_order:"অর্ডার পাওয়া যায়নি। ID পরীক্ষা করুন।",
    offers_lbl:"বিক্রেতার অফার", no_offers:"এখনো অফার নেই — বিক্রেতাদের জানানো হচ্ছে।",
    confirm_receipt:"✓ রসিদ নিশ্চিত", raise_dispute:"⚑ অভিযোগ",
    rate_seller:"★ বিক্রেতাকে রেট করুন", chat_btn:"💬 চ্যাট", upload_unboxing:"🎬 আনবক্সিং",
    sourcing_fee:"সোর্সিং ফি", total_lbl:"মোট", availability_lbl:"প্রাপ্যতা",
    trusted_only:"✦ শুধু বিশ্বস্ত", decade_filter:"দশক",
    authenticated:"✓ প্রমাণিত", sign_in_btn:"সাইন ইন →", join_btn:"বিনামূল্যে যোগ",
    // ── Market ──
    mkt_head:"বিশ্বস্ত বিক্রেতার বাজার", mkt_sub:"প্রকৃত জ্ঞানীদের কাছ থেকে কিনুন।",
    mkt_tab_sale:"বিক্রয়ের জন্য", mkt_tab_list:"ঘড়ি বিক্রি", mkt_tab_sellers:"বিক্রেতা", mkt_tab_apply:"বিক্রেতা হন",
    mkt_filter_trusted:"শুধু বিশ্বস্ত", mkt_send_enq:"জিজ্ঞেস →",
    mkt_all_sellers:"সব বিক্রেতা", mkt_apply_sell:"বিক্রির আবেদন →",
    mkt_submit_listing:"লিস্টিং জমা →", mkt_signin_list:"ঘড়ি বিক্রিতে সাইন ইন",
    mkt_trusted_sellers:"বিশ্বস্ত বিক্রেতা", mkt_avg_rating:"গড় রেটিং", mkt_verified_listings:"যাচাইকৃত",
    mkt_enq_done_h:"অনুসন্ধান পাঠানো হয়েছে।", mkt_watch_photos:"ঘড়ির ছবি",
    mkt_signin_apply:"আবেদনের জন্য সাইন ইন করুন", mkt_handle_lbl:"হ্যান্ডেল",
    mkt_bio_lbl:"আপনার সম্পর্কে", mkt_gov_id_lbl:"সরকারি পরিচয়পত্র",
    mkt_submit_app:"আবেদন জমা দিন →", mkt_checking_id:"ID যাচাই হচ্ছে…",
    mkt_app_done_h:"আবেদন পেয়েছি।", cat_suggest_price:"দাম সুপারিশ করুন →",
    // ── Request ──
    req_sl:"ঘড়ি খুঁজুন", req_head:"আপনি কী খুঁজছেন জানান।",
    req_sub2:"₹99 · 7–14 দিন · আমরা বিক্রেতাদের সাথে যোগাযোগ করি।",
    req_your_reqs:"আপনার অনুরোধসমূহ", req_no_reqs:"এখনো কোনো অনুরোধ নেই।",
    req_new_btn:"+ আরো একটি ঘড়ি চাই",
    // ── Track ──
    trk_sl:"অর্ডার ট্র্যাকিং", trk_head:"আপনার অনুরোধসমূহ।",
    trk_signin_h:"অনুরোধ দেখতে সাইন ইন করুন",
    trk_no_reqs:"এখনো কোনো অনুরোধ নেই।",
    // ── Catalog ──
    cat_sl:"অন্বেষণ", cat_head:"খুঁজুন এবং জানুন।",
    cat_avail:"এখন বিক্রয়ে", cat_all_watches:"সব ঘড়ি",
    cat_want:"আমি এটি চাই →", cat_source:"এটি সোর্স করুন →",
    cat_no_entries:"এখনো কোনো নোন্দ নেই।", cat_no_matches:"কোনো ফলাফল নেই।",
    // ── Auth service ──
    auth_sl:"সত্যতা সেবা", auth_head:"আসল? আমরা বলব।",
    auth_submit_btn:"₹300 দিয়ে জমা →",
    auth_success_h:"অনুরোধ নথিভুক্ত।", auth_success_b:"3–5 দিনে সার্টিফিকেট পাবেন।",
    // ── Services ──
    svc_head2:"আপনার ভিন্টেজ ঘড়ির দরকার বিশেষজ্ঞ হাত।",
    svc_book:"বুক →", svc_submit:"অনুরোধ →",
    svc_success_h:"অনুরোধ পাওয়া গেছে", svc_success_b:"24 ঘণ্টায় WhatsApp-এ যোগাযোগ।",
    nav_catalog:"এক্সপ্লোর", nav_market:"বিক্রি", nav_findtrack:"খোঁজো ও ট্র্যাক",
    nav_chat:"চ্যাট", nav_auth:"যাচাই করুন", nav_services:"সেবা",
    nav_stories:"গল্প", nav_labs:"ল্যাবস", nav_wotd:"WOTD",
    nav_signin:"সাইন ইন", nav_signup:"যোগ দাও",
    hero_browse:"ক্যাটালগ দেখুন →", hero_find:"ঘড়ি খুঁজুন — ₹99",
    hero_signin:"সাইন ইন",
    hero_sub:"ভিন্টেজ ঘড়ির জন্য ভারতের প্ল্যাটফর্ম। কিনুন, বিক্রি করুন, যাচাই করুন — বিশ্বস্ত বিক্রেতা, কমিউনিটি ক্যাটালগ।",
    hero_tag1:"◉ হাজারো ভিন্টেজ ঘড়ি", hero_tag2:"✦ বিশ্বস্ত বিক্রেতা নেটওয়ার্ক", hero_tag3:"◎ সংগ্রহকারীদের আশ্রয়",
    stat_sourced:"ঘড়ি পাওয়া গেছে", stat_catalog:"ক্যাটালগ এন্ট্রি",
    sec_catalog:"ক্যাটালগ", sec_catalog_h:"স্মরণীয় ঘড়িসমূহ।",
    browse_all:"সব দেখুন →", sec_services:"সেবা", sec_services_h:"আমরা কীভাবে সাহায্য করি।",
    sec_process:"প্রক্রিয়া", sec_process_h:"সোর্সিং কীভাবে কাজ করে।",
    sec_sellers:"বিক্রেতা", sec_sellers_h:"বিশ্বস্ত বিক্রেতা।", view_all:"সব দেখুন →",
    svc_find:"ঘড়ি খুঁজুন", svc_find_sub:"বলুন। আমরা খুঁজে দিই।", svc_find_p:"₹99",
    svc_auth:"যাচাই করুন", svc_auth_sub:"যা কিনেছেন তা প্রমাণ করুন।", svc_auth_p:"₹300",
    svc_market:"কিনুন ও বিক্রি", svc_market_sub:"বিশ্বস্ত বিক্রেতাদের বাজার।", svc_market_p:"বিনামূল্যে",
    svc_service:"ঘড়ি সার্ভিসিং", svc_service_sub:"ভিন্টেজ ঘড়ির বিশেষজ্ঞ যত্ন।", svc_service_p:"₹200 থেকে",
    step1_h:"আপনার অনুরোধ পাঠান", step1_b:"কোন ঘড়ি চাই বলুন — মডেল, দশক, অবস্থা, বাজেট। ২ মিনিট লাগে।",
    step2_h:"আমরা নেটওয়ার্কে খুঁজি", step2_b:"আমাদের যাচাইকৃত বিক্রেতারা সারা ভারতে খোঁজ করেন।",
    step3_h:"মিলে যায়", step3_b:"যখন বিক্রেতার কাছে ঘড়ি থাকে, ছবি ও দামসহ নোটিফিকেশন পাবেন।",
    list_watch:"ঘড়ি বিক্রি",
  },
  ta: {
    // ── Common ──
    cancel:"ரத்து செய்", save:"மாற்றங்கள் சேமி", close:"மூடு", back_btn:"பின்",
    send_btn:"அனுப்பு", submit_btn:"சமர்ப்பி", apply_btn:"விண்ணப்பி", track_btn:"கண்காணி",
    brand_lbl:"பிராண்ட்", model_lbl:"மாடல்", decade_lbl:"தசாப்தம்", condition_lbl:"நிலை",
    any_lbl:"எதுவும்", notes_lbl:"குறிப்புகள்", price_lbl:"விலை ₹", email_lbl:"மின்னஞ்சல்",
    name_lbl:"முழு பெயர்", phone_lbl:"WhatsApp / மொபைல்", city_lbl:"நகரம்",
    desc_lbl:"விவரம்", photos_lbl:"புகைப்படங்கள்", filters_lbl:"வடிகட்டிகள்", clear_btn:"அழி",
    search_ph:"பிராண்ட், மாடல் தேடு…", use_credit:"கிரெடிட் பயன்படுத்து →",
    pay_upi:"UPI மூலம் செலுத்து →", or_lbl:"அல்லது", optional_lbl:"(விருப்பம்)", uploading_lbl:"பதிவேற்றுகிறோம்…",
    verified_lbl:"சரிபார்க்கப்பட்ட", trusted_lbl:"நம்பகமான", pending_lbl:"நிலுவை",
    rating_lbl:"மதிப்பீடு", sales_lbl:"விற்பனை", since_lbl:"முதல்", no_updates:"இன்னும் புதுப்பிப்பு இல்லை.",
    no_matches:"எந்த முடிவும் இல்லை.", enquire_btn:"கேளுங்கள் →", all_lbl:"அனைத்தும்",
    for_sale_lbl:"விற்பனைக்கு", wanted_lbl:"வேண்டும்", movement_lbl:"மூவ்மென்ட்",
    seller_lbl:"விற்பனையாளர்", budget_lbl:"பட்ஜெட் ₹", reference_lbl:"குறிப்பு",
    signin_req:"தொடர உள்நுழையுங்கள்", track_req:"என் கோரிக்கையை கண்காணி →",
    no_order:"ஆர்டர் கிடைக்கவில்லை. ID சரிபார்க்கவும்.",
    offers_lbl:"விற்பனையாளர் சலுகைகள்", no_offers:"இன்னும் சலுகை இல்லை — தெரிவிக்கிறோம்.",
    confirm_receipt:"✓ ரசீது உறுதி", raise_dispute:"⚑ புகார்",
    rate_seller:"★ விற்பனையாளரை மதிப்பிடு", chat_btn:"💬 அரட்டை", upload_unboxing:"🎬 அன்பாக்சிங்",
    sourcing_fee:"சோர்சிங் கட்டணம்", total_lbl:"மொத்தம்", availability_lbl:"கிடைக்கும்தன்மை",
    trusted_only:"✦ நம்பகமானவர்கள் மட்டும்", decade_filter:"தசாப்தம்",
    authenticated:"✓ சான்றளிக்கப்பட்ட", sign_in_btn:"உள்நுழை →", join_btn:"இலவசமாக சேர்",
    // ── Market ──
    mkt_head:"நம்பகமான விற்பனையாளர் சந்தை", mkt_sub:"உண்மையில் அறிந்தவர்களிடம் வாங்குங்கள்.",
    mkt_tab_sale:"விற்பனைக்கு", mkt_tab_list:"கடிகாரம் விற்க", mkt_tab_sellers:"விற்பனையாளர்", mkt_tab_apply:"விற்பனையாளர் ஆக",
    mkt_filter_trusted:"நம்பகமானவர்கள் மட்டும்", mkt_send_enq:"கேளுங்கள் →",
    mkt_all_sellers:"அனைத்து விற்பனையாளர்", mkt_apply_sell:"விற்க விண்ணப்பி →",
    mkt_submit_listing:"பட்டியல் சமர்ப்பி →", mkt_signin_list:"விற்க உள்நுழையுங்கள்",
    mkt_trusted_sellers:"நம்பகமான விற்பனையாளர்", mkt_avg_rating:"சராசரி மதிப்பீடு", mkt_verified_listings:"சரிபார்க்கப்பட்ட",
    mkt_enq_done_h:"விசாரணை அனுப்பப்பட்டது.", mkt_watch_photos:"கடிகார புகைப்படங்கள்",
    mkt_signin_apply:"விண்ணப்பிக்க உள்நுழையுங்கள்", mkt_handle_lbl:"கைப்பிடி பெயர்",
    mkt_bio_lbl:"உங்களைப் பற்றி", mkt_gov_id_lbl:"அரசு அடையாள அட்டை",
    mkt_submit_app:"விண்ணப்பம் சமர்ப்பிக்கவும் →", mkt_checking_id:"ID சரிபார்க்கிறோம்…",
    mkt_app_done_h:"விண்ணப்பம் வந்தது.", cat_suggest_price:"விலை பரிந்துரை →",
    // ── Request ──
    req_sl:"கடிகாரம் தேடுங்கள்", req_head:"நீங்கள் என்ன தேடுகிறீர்கள் சொல்லுங்கள்.",
    req_sub2:"₹99 · 7–14 நாட்கள் · விற்பனையாளர்களிடம் தொடர்பு கொள்கிறோம்.",
    req_your_reqs:"உங்கள் கோரிக்கைகள்", req_no_reqs:"இன்னும் கோரிக்கை இல்லை.",
    req_new_btn:"+ இன்னொரு கடிகாரம் கேளுங்கள்",
    // ── Track ──
    trk_sl:"ஆர்டர் கண்காணிப்பு", trk_head:"உங்கள் கோரிக்கைகள்.",
    trk_signin_h:"கோரிக்கைகள் காண உள்நுழையுங்கள்",
    trk_no_reqs:"இன்னும் கோரிக்கை இல்லை.",
    // ── Catalog ──
    cat_sl:"ஆராயுங்கள்", cat_head:"தேடி கண்டறியுங்கள்.",
    cat_avail:"இப்போது விற்பனைக்கு", cat_all_watches:"அனைத்து கடிகாரங்கள்",
    cat_want:"எனக்கு இது வேண்டும் →", cat_source:"இதை சோர்ஸ் செய் →",
    cat_no_entries:"இன்னும் பதிவு இல்லை.", cat_no_matches:"எந்த முடிவும் இல்லை.",
    // ── Auth service ──
    auth_sl:"நம்பகத்தன்மை சேவை", auth_head:"உண்மையா? நாங்கள் சொல்வோம்.",
    auth_submit_btn:"₹300 செலுத்தி சமர்ப்பி →",
    auth_success_h:"கோரிக்கை பதிவாயிற்று.", auth_success_b:"3–5 நாட்களில் சான்றிதழ் கிடைக்கும்.",
    // ── Services ──
    svc_head2:"உங்கள் விண்டேஜ் கடிகாரம் நிபுணர் கை விரும்புகிறது.",
    svc_book:"முன்பதிவு →", svc_submit:"கோரிக்கை →",
    svc_success_h:"கோரிக்கை பெறப்பட்டது", svc_success_b:"24 மணி நேரத்தில் WhatsApp-ல் தொடர்பு.",
    nav_catalog:"ஆராயுங்கள்", nav_market:"விற்கவும்", nav_findtrack:"தேடு & கண்காணி",
    nav_chat:"அரட்டை", nav_auth:"சான்றளிக்கவும்", nav_services:"சேவைகள்",
    nav_stories:"கதைகள்", nav_labs:"ஆய்வகங்கள்", nav_wotd:"WOTD",
    nav_signin:"உள்நுழை", nav_signup:"சேரவும்",
    hero_browse:"கேட்டலாக் பார்க்க →", hero_find:"கடிகாரம் தேடுங்கள் — ₹99",
    hero_signin:"உள்நுழை",
    hero_sub:"விண்டேஜ் கடிகாரங்களுக்கான இந்தியாவின் தளம். வாங்கவும், விற்கவும், சான்றளிக்கவும் — நம்பகமான விற்பனையாளர்கள், சமூக கேட்டலாக்.",
    hero_tag1:"◉ ஆயிரக்கணக்கான விண்டேஜ் கடிகாரங்கள்", hero_tag2:"✦ நம்பகமான விற்பனையாளர் நெட்வொர்க்", hero_tag3:"◎ சேகரிப்பாளர்களின் தளம்",
    stat_sourced:"கடிகாரங்கள் கிடைத்தன", stat_catalog:"கேட்டலாக் பதிவுகள்",
    sec_catalog:"கேட்டலாக்", sec_catalog_h:"நினைவில் வைக்கத்தக்க கடிகாரங்கள்.",
    browse_all:"அனைத்தையும் பார்க்கவும் →", sec_services:"சேவைகள்", sec_services_h:"நாங்கள் எவ்வாறு உதவுகிறோம்.",
    sec_process:"செயல்முறை", sec_process_h:"சோர்சிங் எவ்வாறு செயல்படுகிறது.",
    sec_sellers:"விற்பனையாளர்கள்", sec_sellers_h:"நம்பகமான விற்பனையாளர்கள்.", view_all:"அனைத்தையும் பார்க்கவும் →",
    svc_find:"கடிகாரம் தேடுங்கள்", svc_find_sub:"சொல்லுங்கள். நாங்கள் தேடுகிறோம்.", svc_find_p:"₹99",
    svc_auth:"சான்றளிக்கவும்", svc_auth_sub:"வாங்கியதை சான்றளிக்கவும்.", svc_auth_p:"₹300",
    svc_market:"வாங்கவும் & விற்கவும்", svc_market_sub:"நம்பகமான விற்பனையாளர்களின் சந்தை.", svc_market_p:"இலவசம்",
    svc_service:"கடிகார சேவை", svc_service_sub:"விண்டேஜ் கடிகாரங்களுக்கு நிபுணர் கவனிப்பு.", svc_service_p:"₹200 முதல்",
    step1_h:"உங்கள் கோரிக்கையை அனுப்புங்கள்", step1_b:"எந்த கடிகாரம் வேண்டும் சொல்லுங்கள் — மாடல், தசாப்தம், நிலை, பட்ஜெட். 2 நிமிடங்கள்.",
    step2_h:"நாங்கள் நெட்வொர்க்கில் தேடுகிறோம்", step2_b:"எங்கள் சரிபார்க்கப்பட்ட விற்பனையாளர்கள் இந்தியா முழுவதும் தேடுகிறார்கள்.",
    step3_h:"பொருத்தம் கிடைக்கும்", step3_b:"விற்பனையாளரிடம் கடிகாரம் இருக்கும்போது, புகைப்படம் மற்றும் விலையுடன் அறிவிப்பு வரும்.",
    list_watch:"கடிகாரம் விற்கவும்",
  },
  kn: {
    // ── Common ──
    cancel:"ರದ್ದು ಮಾಡಿ", save:"ಬದಲಾವಣೆ ಉಳಿಸಿ", close:"ಮುಚ್ಚಿ", back_btn:"ಹಿಂದೆ",
    send_btn:"ಕಳುಹಿಸಿ", submit_btn:"ಸಲ್ಲಿಸಿ", apply_btn:"ಅರ್ಜಿ ಸಲ್ಲಿಸಿ", track_btn:"ಟ್ರ್ಯಾಕ್",
    brand_lbl:"ಬ್ರ್ಯಾಂಡ್", model_lbl:"ಮಾಡೆಲ್", decade_lbl:"ದಶಕ", condition_lbl:"ಸ್ಥಿತಿ",
    any_lbl:"ಯಾವುದಾದರೂ", notes_lbl:"ಟಿಪ್ಪಣಿ", price_lbl:"ಬೆಲೆ ₹", email_lbl:"ಇಮೇಲ್",
    name_lbl:"ಪೂರ್ಣ ಹೆಸರು", phone_lbl:"WhatsApp / ಮೊಬೈಲ್", city_lbl:"ನಗರ",
    desc_lbl:"ವಿವರಣೆ", photos_lbl:"ಫೋಟೋ", filters_lbl:"ಫಿಲ್ಟರ್", clear_btn:"ತೆರವು",
    search_ph:"ಬ್ರ್ಯಾಂಡ್, ಮಾಡೆಲ್ ಹುಡುಕಿ…", use_credit:"ಕ್ರೆಡಿಟ್ ಬಳಸಿ →",
    pay_upi:"UPI ಮೂಲಕ ಪಾವತಿ →", or_lbl:"ಅಥವಾ", optional_lbl:"(ಐಚ್ಛಿಕ)", uploading_lbl:"ಅಪ್‌ಲೋಡ್ ಆಗುತ್ತಿದೆ…",
    verified_lbl:"ಪರಿಶೀಲಿಸಲಾಗಿದೆ", trusted_lbl:"ವಿಶ್ವಾಸಾರ್ಹ", pending_lbl:"ಬಾಕಿ",
    rating_lbl:"ರೇಟಿಂಗ್", sales_lbl:"ಮಾರಾಟ", since_lbl:"ಯಿಂದ", no_updates:"ಇನ್ನು ಅಪ್‌ಡೇಟ್ ಇಲ್ಲ.",
    no_matches:"ಯಾವ ಫಲಿತಾಂಶವೂ ಇಲ್ಲ.", enquire_btn:"ವಿಚಾರಿಸಿ →", all_lbl:"ಎಲ್ಲಾ",
    for_sale_lbl:"ಮಾರಾಟಕ್ಕೆ", wanted_lbl:"ಬೇಕಾಗಿದೆ", movement_lbl:"ಮೂವ್‌ಮೆಂಟ್",
    seller_lbl:"ಮಾರಾಟಗಾರ", budget_lbl:"ಬಜೆಟ್ ₹", reference_lbl:"ಉಲ್ಲೇಖ",
    signin_req:"ಮುಂದೆ ಸಾಗಲು ಸೈನ್ ಇನ್ ಮಾಡಿ", track_req:"ನನ್ನ ಬೇಡಿಕೆ ಟ್ರ್ಯಾಕ್ →",
    no_order:"ಆರ್ಡರ್ ಕಂಡುಬಂದಿಲ್ಲ. ID ಪರಿಶೀಲಿಸಿ.",
    offers_lbl:"ಮಾರಾಟಗಾರರ ಕೊಡುಗೆಗಳು", no_offers:"ಇನ್ನು ಕೊಡುಗೆ ಇಲ್ಲ — ತಿಳಿಸುತ್ತಿದ್ದೇವೆ.",
    confirm_receipt:"✓ ರಸೀದಿ ದೃಢಪಡಿಸಿ", raise_dispute:"⚑ ದೂರು",
    rate_seller:"★ ಮಾರಾಟಗಾರನ್ನು ರೇಟ್ ಮಾಡಿ", chat_btn:"💬 ಚಾಟ್", upload_unboxing:"🎬 ಅನ್‌ಬಾಕ್ಸಿಂಗ್",
    sourcing_fee:"ಸೋರ್ಸಿಂಗ್ ಶುಲ್ಕ", total_lbl:"ಒಟ್ಟು", availability_lbl:"ಲಭ್ಯತೆ",
    trusted_only:"✦ ವಿಶ್ವಾಸಾರ್ಹರು ಮಾತ್ರ", decade_filter:"ದಶಕ",
    authenticated:"✓ ಪ್ರಮಾಣೀಕೃತ", sign_in_btn:"ಸೈನ್ ಇನ್ →", join_btn:"ಉಚಿತ ಸೇರಿ",
    // ── Market ──
    mkt_head:"ವಿಶ್ವಾಸಾರ್ಹ ಮಾರಾಟಗಾರರ ಮಾರುಕಟ್ಟೆ", mkt_sub:"ನಿಜವಾಗಿ ತಿಳಿದವರಿಂದ ಖರೀದಿಸಿ.",
    mkt_tab_sale:"ಮಾರಾಟಕ್ಕೆ", mkt_tab_list:"ಗಡಿಯಾರ ಮಾರಿ", mkt_tab_sellers:"ಮಾರಾಟಗಾರರು", mkt_tab_apply:"ಮಾರಾಟಗಾರ ಆಗಿ",
    mkt_filter_trusted:"ವಿಶ್ವಾಸಾರ್ಹರು ಮಾತ್ರ", mkt_send_enq:"ವಿಚಾರಿಸಿ →",
    mkt_all_sellers:"ಎಲ್ಲಾ ಮಾರಾಟಗಾರರು", mkt_apply_sell:"ಮಾರಾಟಕ್ಕೆ ಅರ್ಜಿ →",
    mkt_submit_listing:"ಪಟ್ಟಿ ಸಲ್ಲಿಸಿ →", mkt_signin_list:"ಮಾರಲು ಸೈನ್ ಇನ್ ಮಾಡಿ",
    mkt_trusted_sellers:"ವಿಶ್ವಾಸಾರ್ಹ ಮಾರಾಟಗಾರರು", mkt_avg_rating:"ಸರಾಸರಿ ರೇಟಿಂಗ್", mkt_verified_listings:"ಪರಿಶೀಲಿಸಲಾಗಿದೆ",
    mkt_enq_done_h:"ವಿಚಾರಣೆ ಕಳುಹಿಸಲಾಗಿದೆ.", mkt_watch_photos:"ಗಡಿಯಾರ ಫೋಟೋಗಳು",
    mkt_signin_apply:"ಅರ್ಜಿಗೆ ಸೈನ್ ಇನ್ ಮಾಡಿ", mkt_handle_lbl:"ಹ್ಯಾಂಡಲ್",
    mkt_bio_lbl:"ನಿಮ್ಮ ಬಗ್ಗೆ", mkt_gov_id_lbl:"ಸರ್ಕಾರಿ ಗುರುತಿನ ಚೀಟಿ",
    mkt_submit_app:"ಅರ್ಜಿ ಸಲ್ಲಿಸಿ →", mkt_checking_id:"ID ಪರಿಶೀಲಿಸಲಾಗುತ್ತಿದೆ…",
    mkt_app_done_h:"ಅರ್ಜಿ ಸ್ವೀಕರಿಸಲಾಗಿದೆ.", cat_suggest_price:"ಬೆಲೆ ಸೂಚಿಸಿ →",
    // ── Request ──
    req_sl:"ಗಡಿಯಾರ ಹುಡುಕಿ", req_head:"ನೀವು ಏನು ಹುಡುಕುತ್ತಿದ್ದೀರಿ ಹೇಳಿ.",
    req_sub2:"₹99 · 7–14 ದಿನ · ನಾವು ಮಾರಾಟಗಾರರನ್ನು ಸಂಪರ್ಕಿಸುತ್ತೇವೆ.",
    req_your_reqs:"ನಿಮ್ಮ ಬೇಡಿಕೆಗಳು", req_no_reqs:"ಇನ್ನು ಬೇಡಿಕೆ ಇಲ್ಲ.",
    req_new_btn:"+ ಇನ್ನೊಂದು ಗಡಿಯಾರ ಕೇಳಿ",
    // ── Track ──
    trk_sl:"ಆರ್ಡರ್ ಟ್ರ್ಯಾಕಿಂಗ್", trk_head:"ನಿಮ್ಮ ಬೇಡಿಕೆಗಳು.",
    trk_signin_h:"ಬೇಡಿಕೆ ನೋಡಲು ಸೈನ್ ಇನ್ ಮಾಡಿ",
    trk_no_reqs:"ಇನ್ನು ಬೇಡಿಕೆ ಇಲ್ಲ.",
    // ── Catalog ──
    cat_sl:"ಅನ್ವೇಷಿಸಿ", cat_head:"ಹುಡುಕಿ ಕಂಡುಕೊಳ್ಳಿ.",
    cat_avail:"ಈಗ ಮಾರಾಟಕ್ಕೆ", cat_all_watches:"ಎಲ್ಲಾ ಗಡಿಯಾರಗಳು",
    cat_want:"ನನಗೆ ಇದು ಬೇಕು →", cat_source:"ಇದನ್ನು ಸೋರ್ಸ್ ಮಾಡಿ →",
    cat_no_entries:"ಇನ್ನು ನಮೂದು ಇಲ್ಲ.", cat_no_matches:"ಯಾವ ಫಲಿತಾಂಶವೂ ಇಲ್ಲ.",
    // ── Auth service ──
    auth_sl:"ಅಧಿಕೃತತೆ ಸೇವೆ", auth_head:"ನಿಜವೇ? ನಾವು ಹೇಳುತ್ತೇವೆ.",
    auth_submit_btn:"₹300 ಪಾವತಿ ಮತ್ತು ಸಲ್ಲಿಸಿ →",
    auth_success_h:"ಬೇಡಿಕೆ ದಾಖಲಾಗಿದೆ.", auth_success_b:"3–5 ದಿನಗಳಲ್ಲಿ ಪ್ರಮಾಣಪತ್ರ ಸಿಗುತ್ತದೆ.",
    // ── Services ──
    svc_head2:"ನಿಮ್ಮ ವಿಂಟೇಜ್ ಗಡಿಯಾರಕ್ಕೆ ತಜ್ಞ ಕೈ ಬೇಕು.",
    svc_book:"ಬುಕ್ →", svc_submit:"ಬೇಡಿಕೆ →",
    svc_success_h:"ಬೇಡಿಕೆ ಸ್ವೀಕರಿಸಲಾಗಿದೆ", svc_success_b:"24 ಗಂಟೆಗಳಲ್ಲಿ WhatsApp ನಲ್ಲಿ ಸಂಪರ್ಕ.",
    nav_catalog:"ಅನ್ವೇಷಿಸಿ", nav_market:"ಮಾರಿ", nav_findtrack:"ಹುಡುಕಿ & ಟ್ರ್ಯಾಕ್",
    nav_chat:"ಚಾಟ್", nav_auth:"ಪ್ರಮಾಣೀಕರಿಸಿ", nav_services:"ಸೇವೆಗಳು",
    nav_stories:"ಕಥೆಗಳು", nav_labs:"ಲ್ಯಾಬ್ಸ್", nav_wotd:"WOTD",
    nav_signin:"ಸೈನ್ ಇನ್", nav_signup:"ಸೇರಿ",
    hero_browse:"ಕ್ಯಾಟಲಾಗ್ ನೋಡಿ →", hero_find:"ಗಡಿಯಾರ ಹುಡುಕಿ — ₹99",
    hero_signin:"ಸೈನ್ ಇನ್",
    hero_sub:"ವಿಂಟೇಜ್ ಗಡಿಯಾರಗಳಿಗಾಗಿ ಭಾರತದ ವೇದಿಕೆ. ಖರೀದಿಸಿ, ಮಾರಿ, ಪ್ರಮಾಣೀಕರಿಸಿ — ವಿಶ್ವಾಸಾರ್ಹ ಮಾರಾಟಗಾರರು, ಸಮುದಾಯ ಕ್ಯಾಟಲಾಗ್.",
    hero_tag1:"◉ ಸಾವಿರಾರು ವಿಂಟೇಜ್ ಗಡಿಯಾರಗಳು", hero_tag2:"✦ ವಿಶ್ವಾಸಾರ್ಹ ಮಾರಾಟಗಾರ ನೆಟ್‌ವರ್ಕ್", hero_tag3:"◎ ಸಂಗ್ರಾಹಕರ ಆಶ್ರಯ",
    stat_sourced:"ಗಡಿಯಾರಗಳು ದೊರೆತವು", stat_catalog:"ಕ್ಯಾಟಲಾಗ್ ನಮೂದುಗಳು",
    sec_catalog:"ಕ್ಯಾಟಲಾಗ್", sec_catalog_h:"ನೆನಪಿನಲ್ಲಿ ಉಳಿಯುವ ಗಡಿಯಾರಗಳು.",
    browse_all:"ಎಲ್ಲವನ್ನು ನೋಡಿ →", sec_services:"ಸೇವೆಗಳು", sec_services_h:"ನಾವು ಹೇಗೆ ಸಹಾಯ ಮಾಡುತ್ತೇವೆ.",
    sec_process:"ಪ್ರಕ್ರಿಯೆ", sec_process_h:"ಸೋರ್ಸಿಂಗ್ ಹೇಗೆ ಕಾರ್ಯನಿರ್ವಹಿಸುತ್ತದೆ.",
    sec_sellers:"ಮಾರಾಟಗಾರರು", sec_sellers_h:"ವಿಶ್ವಾಸಾರ್ಹ ಮಾರಾಟಗಾರರು.", view_all:"ಎಲ್ಲವನ್ನು ನೋಡಿ →",
    svc_find:"ಗಡಿಯಾರ ಹುಡುಕಿ", svc_find_sub:"ಹೇಳಿ. ನಾವು ಹುಡುಕುತ್ತೇವೆ.", svc_find_p:"₹99",
    svc_auth:"ಪ್ರಮಾಣೀಕರಿಸಿ", svc_auth_sub:"ಖರೀದಿಸಿದ್ದನ್ನು ಪ್ರಮಾಣೀಕರಿಸಿ.", svc_auth_p:"₹300",
    svc_market:"ಖರೀದಿಸಿ & ಮಾರಿ", svc_market_sub:"ವಿಶ್ವಾಸಾರ್ಹ ಮಾರಾಟಗಾರರ ಮಾರುಕಟ್ಟೆ.", svc_market_p:"ಉಚಿತ",
    svc_service:"ಗಡಿಯಾರ ಸರ್ವಿಸಿಂಗ್", svc_service_sub:"ವಿಂಟೇಜ್ ಗಡಿಯಾರಗಳಿಗೆ ತಜ್ಞ ಆರೈಕೆ.", svc_service_p:"₹200 ರಿಂದ",
    step1_h:"ನಿಮ್ಮ ವಿನಂತಿ ಕಳಿಸಿ", step1_b:"ಯಾವ ಗಡಿಯಾರ ಬೇಕು ಹೇಳಿ — ಮಾಡೆಲ್, ದಶಕ, ಸ್ಥಿತಿ, ಬಜೆಟ್. 2 ನಿಮಿಷ.",
    step2_h:"ನಾವು ನೆಟ್‌ವರ್ಕ್‌ನಲ್ಲಿ ಹುಡುಕುತ್ತೇವೆ", step2_b:"ನಮ್ಮ ಪರಿಶೀಲಿತ ಮಾರಾಟಗಾರರು ಭಾರತ ತುಂಬ ಹುಡುಕುತ್ತಾರೆ.",
    step3_h:"ಹೊಂದಾಣಿಕೆ ಆಗುತ್ತದೆ", step3_b:"ಮಾರಾಟಗಾರನ ಬಳಿ ಗಡಿಯಾರ ಇದ್ದಾಗ, ಫೋಟೋ ಮತ್ತು ಬೆಲೆಯೊಂದಿಗೆ ಅಧಿಸೂಚನೆ ಬರುತ್ತದೆ.",
    list_watch:"ಗಡಿಯಾರ ಮಾರಿ",
  },
};

export function t(key, lang) {
  return S[lang]?.[key] ?? S.en[key] ?? key;
}

export function getHeroLines(lang) {
  return HERO_LINES[lang] || HERO_LINES.en;
}

export function getLangLabel(lang) {
  return LANG_OPTIONS.find(o=>o.code===lang)?.native || "EN";
}
